import React, { useEffect, useState, useRef, Children, Fragment, lazy, memo, createElement, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Package, Star, X, Minus, MessageSquare, AlertCircle, Brain, DollarSign, BookX, Video, HardHat, Flame, Building2, CircuitBoard, Cpu, Settings, Target, TrendingUp, Award, GitBranch, Lightbulb, Headphones, ChevronLeft, ChevronRight, Eye, Ticket, Ruler, Search, Lock, BarChart3, Banknote, Globe, BookOpen } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { VerticalCarousel } from '../components/VerticalCarousel';
import { HorizontalCarousel } from '../components/HorizontalCarousel';
import { BookStackFan } from '../components/BookStackFan';
const heroBonusBooks = [{
  src: 'https://cdn.magicpatterns.com/uploads/8hCkxvV695qrhWRJUeEp3V/Diseno_sin_titulo_(7).webp',
  alt: 'PLC MASTER',
  position: 'top-left' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jMegiRm2EkcJHgkYsr55Yo/A_detailed,_premium_202604151029.png',
  alt: 'Variadores y Sensores',
  position: 'top-right' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/urAD5W6FULjmkd4Bkb2VES/Diseno_sin_titulo_(7)_(2).webp',
  alt: 'Bono 3 Protocolo de Energía',
  position: 'bottom-left' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vsVq8bhiUB6NysBrB8mjAQ/Diseno_sin_titulo_(7)_(1).webp',
  alt: 'Curso Paneles Solares',
  position: 'bottom-right' as const
}];
const heroPositionConfig = {
  'top-left': {
    x: '-55%',
    y: '-22%',
    rotate: -6,
    z: 12
  },
  'top-right': {
    x: '55%',
    y: '-22%',
    rotate: 6,
    z: 11
  },
  'bottom-left': {
    x: '-60%',
    y: '18%',
    rotate: -4,
    z: 10
  },
  'bottom-right': {
    x: '60%',
    y: '18%',
    rotate: 4,
    z: 9
  }
};
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-54" data-mp="3e823pe8drkqm">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-55" data-mp="396w6b33rz9ar">
        <span className="font-medium text-gray-900 pr-4" data-id="element-56" data-mp="3j63p45ta3w59">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-57" data-mp="ruo46ufe9no" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-58" data-mp="rcm0tl3117u" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-59" data-mp="3sw6nklj662az">
          <p className="text-gray-700 leading-relaxed" data-id="element-60" data-mp="ap8qj2jikxwz">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    let interval: NodeJS.Timeout;
    const timer = setTimeout(() => {
      interval = setInterval(() => {
        setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
      }, 4000);
    }, 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-61" data-mp="2t12hpqar1gb8">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-62" data-mp="2j5sex0cj14we">
        <div className="aspect-[3/4] relative" data-id="element-63" data-mp="hd49ckceu5dg">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" loading="lazy" decoding="async" width={640} height={853} data-id="element-64" data-mp="ha8ckzcatmql" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-65" data-mp="ymeuc5w31u0j">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-66" data-mp="13kglzf7kr1nm">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-67" data-mp="6vs8mw6t02kj">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-68" data-mp="awu9tika6op5" data-mpi={index} />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [urgencySpots, setUrgencySpots] = useState(15);
  const [upsellSpots, setUpsellSpots] = useState(() => {
    const saved = localStorage.getItem('upsellSpots');
    return saved ? Math.max(2, parseInt(saved, 10)) : 8;
  });
  const [mainBuyers, setMainBuyers] = useState(() => {
    const saved = localStorage.getItem('mainBuyers');
    return saved ? parseInt(saved, 10) : 3251;
  });
  const [mainSpots, setMainSpots] = useState(15);
  const [comboBuyers, setComboBuyers] = useState(() => {
    const saved = localStorage.getItem('comboBuyers');
    return saved ? parseInt(saved, 10) : 5162;
  });
  const [comboSpots, setComboSpots] = useState(7);
  // Persist buyers and upsellSpots to localStorage
  useEffect(() => {
    localStorage.setItem('mainBuyers', mainBuyers.toString());
  }, [mainBuyers]);
  useEffect(() => {
    localStorage.setItem('comboBuyers', comboBuyers.toString());
  }, [comboBuyers]);
  useEffect(() => {
    localStorage.setItem('upsellSpots', upsellSpots.toString());
  }, [upsellSpots]);
  useEffect(() => {
    let viewerInterval: NodeJS.Timeout;
    let mainBuyersInterval: NodeJS.Timeout;
    let comboBuyersInterval: NodeJS.Timeout;
    let urgencySpotsInterval: NodeJS.Timeout;
    let upsellSpotsInterval: NodeJS.Timeout;
    // Defer non-critical state updates to reduce TBT
    const timer = setTimeout(() => {
      // Viewers fluctuation (40-63 range)
      viewerInterval = setInterval(() => {
        setViewers(prev => {
          const change = Math.floor(Math.random() * 5) - 2;
          return Math.max(40, Math.min(63, prev + change));
        });
      }, 3000 + Math.random() * 4000);
      // Profesionales (social proof): increase 1-3 every 10s
      mainBuyersInterval = setInterval(() => {
        const increment = Math.floor(Math.random() * 3) + 1;
        setMainBuyers(prev => prev + increment);
      }, 10000);
      comboBuyersInterval = setInterval(() => {
        const increment = Math.floor(Math.random() * 3) + 1;
        setComboBuyers(prev => prev + increment);
      }, 10000);
      // Card 1 cupos (scarcity): decrease 1 every 15-40s, min 2
      urgencySpotsInterval = setInterval(() => {
        setUrgencySpots(prev => Math.max(2, prev - 1));
      }, 15000 + Math.random() * 25000);
      // Upsell cupos (scarcity): decrease 1 every 120-180s, min 2
      upsellSpotsInterval = setInterval(() => {
        setUpsellSpots(prev => Math.max(2, prev - 1));
      }, 120000 + Math.random() * 60000);
    }, 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(viewerInterval);
      clearInterval(mainBuyersInterval);
      clearInterval(comboBuyersInterval);
      clearInterval(urgencySpotsInterval);
      clearInterval(upsellSpotsInterval);
    };
  }, []);
  return {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  };
}
// Current Date Hook
function useCurrentDate() {
  const [dateString, setDateString] = useState('');
  const [shortDate, setShortDate] = useState('');
  const [weekdayDate, setWeekdayDate] = useState('');
  useEffect(() => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    setDateString(now.toLocaleDateString('es-AR', options));
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    setShortDate(`${dd}/${mm}/${yyyy}`);
    // Weekday + day + month (e.g. "Lunes 16 de Marzo")
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    setWeekdayDate(`${weekdays[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`);
  }, []);
  return {
    dateString,
    shortDate,
    weekdayDate
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-69" data-mp="2rzz8k0igj8us">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-70" data-mp="2rz7krbv3qff2">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-71" data-mp="2ixpytcv0jjig">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-72" data-mp="3bo89p01zap6f">
          <p data-id="element-73" data-mp="36ntzbbcwn358">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-74" data-mp="31qtsgy88dwd2">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-75" data-mp="31sem8lmq1b9v">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-76" data-mp="1vbt6nlcx6uk7" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-77" data-mp="1qadhbh3pm1w0">
          <X className="w-5 h-5" data-id="element-78" data-mp="1rtlxab65nby6" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-79" data-mp="1wrx8ap86rshz">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-80" data-mp="1wq421b1hjaai">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-81" data-mp="1is5m417in5sw" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-82" data-mp="190i22ldagr1g">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_5px_rgba(250,204,21,0.4)]' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-83" data-mp="101ehte2dy5ld" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-84" data-mp="v1id1clv6a4r" />
        {error && <p className="text-red-500 text-sm" data-id="element-85" data-mp="lf7g4dag2ep0">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-86" data-mp="gfmbpnci9e4w">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Offer Selector Component
function OfferSelector({
  selected,
  onChange
}: {
  selected: 'solo' | 'combo';
  onChange: (val: 'solo' | 'combo') => void;
}) {
  return <div className="flex flex-col items-center mb-12 w-full max-w-4xl mx-auto px-2" data-id="element-87" data-mp="146rry3mcy8fq">
      <div className="flex flex-col md:flex-row w-full gap-4" data-id="element-88" data-mp="z7xbv9fryxzb">
        {/* Solo Maestro Button */}
        <motion.button whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.98
      }} onClick={() => onChange('solo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight ${selected === 'solo' ? 'bg-[#0a1a3a] border-blue-500 text-white animate-neon-blue z-10' : 'bg-[#0f0f0f] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-89" data-mp="1d3iyxb2g77p3">
          {selected === 'solo' && <motion.div layoutId="selector-dot" className="absolute top-3 left-3 w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.8)]" data-id="element-90" data-mp="3hdgaogelb2v3" />}
          SOLO MAESTRO ELÉCTRICO
        </motion.button>

        {/* Combo Button */}
        <motion.button whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.98
      }} onClick={() => onChange('combo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight flex flex-col items-center justify-center gap-1 ${selected === 'combo' ? 'bg-black border-[#FFFF00] text-white animate-neon-yellow z-10' : 'bg-[#0a0a0a] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-91" data-mp="2tlt0591my4ez">
          {selected === 'combo' && <motion.div layoutId="selector-dot" className="absolute top-3 left-3 w-2.5 h-2.5 bg-[#FFFF00] rounded-full shadow-[0_0_8px_rgba(255,255,0,0.8)]" data-id="element-92" data-mp="1tg8hlgrgiwhi" />}
          {/* Embedded Badge */}
          <div className="absolute -top-3 right-4 bg-[#FFFF00] text-black text-[10px] font-black px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,255,0,0.4)] z-10 whitespace-nowrap uppercase tracking-widest animate-badge-glow" data-id="element-93" data-mp="1jlyo1vqs3s57">
            MÁS ELEGIDO
          </div>

          {/* Engraved Seal Icon */}
          <div className="absolute bottom-3 right-3 opacity-20 group-hover:opacity-40 transition-opacity" data-id="element-94" data-mp="15q4po6nmsl6c">
            <Award className="w-8 h-8 md:w-10 md:h-10 text-[#FFFF00]" strokeWidth={1} data-id="element-95" data-mp="302b196kft3z6" />
          </div>

          <span data-id="element-96" data-mp="2v3856bmfq83c">COMBO MAESTRO ELÉCTRICO + PLC MASTER</span>
        </motion.button>
      </div>
    </div>;
}
// Lazy Video Component
function LazyVideo({
  src,
  className
}: {
  src: string;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasLoaded(true);
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      } else {
        if (videoRef.current) {
          videoRef.current.pause();
        }
      }
    }, {
      threshold: 0.1
    });
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <video ref={videoRef} className={className} loop muted playsInline preload="none" data-id="element-97" data-mp="3svy43z5qtyul">
      {hasLoaded && <source src={src} type="video/mp4" data-id="element-98" data-mp="2ci7uydzroke3" />}
    </video>;
}
export function LandingPage() {
  const shouldReduceMotion = useReducedMotion();
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  // Removed JS-based preload as it is now handled statically in index.tsx for better LCP
  const {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  } = useDynamicStats();
  const {
    dateString: currentDate,
    shortDate,
    weekdayDate
  } = useCurrentDate();
  const [headerVisible, setHeaderVisible] = useState(false);
  const selectedOffer: 'solo' | 'v2' | 'combo' = 'solo';
  const setSelectedOffer = (_offer: 'solo' | 'v2' | 'combo') => undefined;
  const offerDetails = {
    solo: {
      price: '$19.990',
      originalPrice: '$39.980',
      priceValue: 19990,
      priceClass: 'text-[#172554]',
      preview: 'https://cdn.magicpatterns.com/uploads/fNPyYViHFnYPFQdMEJeFpf/Manual_Maestro_Electrico_Industrial.webp',
      previewAlt: 'Manual Maestro Eléctrico Industrial',
      cta: '¡SÍ! QUIERO MAESTRO ELÉCTRICO →'
    },
    v2: {
      price: '$26.990',
      originalPrice: '$53.980',
      priceValue: 26990,
      priceClass: 'text-[#9A6700]',
      preview: 'https://cdn.magicpatterns.com/uploads/g3bvweWEiYsuRycQ3c7yWp/Maestro_Electrico_2.0__Manuales_Industriales.webp',
      previewAlt: 'Manuales industriales Maestro Eléctrico 2.0',
      cta: '¡SÍ! QUIERO MAESTRO ELÉCTRICO 2.0 →'
    },
    combo: {
      price: '$34.990',
      originalPrice: '$69.980',
      priceValue: 34990,
      priceClass: 'text-[#00A859]',
      preview: 'https://cdn.magicpatterns.com/uploads/wXGMYaU7YLmiDQsfPZqDhk/Combo_industrial_completo_en_cuatro_manuales.webp',
      previewAlt: 'Combo Industrial Completo en cuatro manuales',
      cta: '¡QUIERO EL COMBO COMPLETO! →'
    }
  } as const;
  const activeOffer = offerDetails[selectedOffer];
  const offerModalDetails = {
    solo: {
      title: 'Maestro Eléctrico',
      summary: 'Manual Maestro Eléctrico + 5 bonos incluidos',
      benefits: ['Manual digital', '5 bonos incluidos', 'Acceso inmediato'],
      included: ['Manual Maestro Eléctrico', 'Eficiencia en Motores', 'Plantillas de Presupuesto', 'Protocolo de Energía', 'Curso de Paneles Solares', 'Reglamentaciones AEA']
    },
    v2: {
      title: 'Maestro Eléctrico 2.0',
      summary: '',
      benefits: [],
      included: []
    },
    combo: {
      title: 'Combo Industrial Completo',
      summary: '',
      benefits: [],
      included: []
    }
  } as const;
  const activeModalDetails = offerModalDetails[selectedOffer];
  const isOfferModalOpen = false;
  const setIsOfferModalOpen = (_open: boolean) => undefined;
  const [fearsVisible, setFearsVisible] = useState(false);
  const [problemVisible, setProblemVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [isHeroFanRevealed, setIsHeroFanRevealed] = useState(false);
  const [isReadyForBonuses, setIsReadyForBonuses] = useState(false);
  // Trigger hero explosion and bonus loading after a delay to prioritize LCP
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReadyForBonuses(true);
      // Auto-reveal after a bit more time for visual impact
      setTimeout(() => setIsHeroFanRevealed(true), 500);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [localReview, setLocalReview] = useState<{
    name: string;
    text: string;
    stars: number;
  } | null>(null);
  const [activeBonusIndex, setActiveBonusIndex] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const marqueeResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const isDragging = useRef(false);
  // Animation variants for the fan effect
  const fanVariants = {
    hidden: {
      x: '0%',
      y: '0%',
      rotate: 0,
      opacity: 0
    },
    visible: (pos: keyof typeof heroPositionConfig) => ({
      x: heroPositionConfig[pos].x,
      y: heroPositionConfig[pos].y,
      rotate: heroPositionConfig[pos].rotate,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    })
  };
  // Marquee pause/resume handlers
  const pauseMarquee = () => {
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Drag handlers for manual swipe
  const handleDragStart = (clientX: number) => {
    const container = marqueeContainerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragScrollLeft.current = container.scrollLeft;
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging.current) return;
    const container = marqueeContainerRef.current;
    if (!container) return;
    const dx = clientX - dragStartX.current;
    container.scrollLeft = dragScrollLeft.current - dx;
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Handle bonus scroll for pagination dots
  const handleBonusScroll = () => {
    if (!bonusScrollRef.current) return;
    const container = bonusScrollRef.current;
    const scrollPosition = container.scrollLeft;
    const cardWidth = container.offsetWidth * 0.85; // Approximate card width on mobile
    const newIndex = Math.round(scrollPosition / cardWidth);
    if (newIndex !== activeBonusIndex) {
      setActiveBonusIndex(newIndex);
    }
  };
  // Argentine chat reviews pool (30+)
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la forma de laburar. Antes conectaba de memoria y ahora entiendo qué hace cada cable 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano, nada de vueltas teóricas al pedo. Los diagramas están claritos',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con un cliente. Tenía que automatizar un tanque y no le encontraba la vuelta. Con esto lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso hermano. Solo con la planilla de presupuesto ya se paga solo. La uso para todos los laburos',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la cabeza mal. Antes adivinaba, ahora sé lo que hago ⚡',
    time: '16:22',
    verified: true
  }, {
    name: 'Ricardo A.',
    msg: 'Soy técnico hace 10 pirulos y siempre se aprende algo nuevo. El protocolo de verificación es un caño, lo uso siempre',
    time: '20:11',
    verified: true
  }, {
    name: 'Marcelo D.',
    msg: 'Al principio me costó un toque porque venía de cero, pero leyéndolo tranqui se entiende todo. Me salvó con un tablero de bombas',
    time: '08:45',
    verified: false
  }, {
    name: 'Fernando T.',
    msg: 'Es técnico pero se deja leer. No es esos libros aburridos de ingeniería que te dormís. Es para el que está en la obra posta',
    time: '13:20',
    verified: true
  }, {
    name: 'Jorge L.',
    msg: 'Lo imprimí y lo llevo en la caja de herramientas. Cuando tengo una duda lo miro ahí nomás. Re práctico',
    time: '17:55',
    verified: true
  }, {
    name: 'Sergio F.',
    msg: 'Está muy completo che. Estaría bueno que tenga videos, pero las explicaciones escritas son un golazo igual',
    time: '10:30',
    verified: true
  }, {
    name: 'Diego P.',
    msg: 'Ni hablar, lo mejor que compré en años. Ahora los tableros no me asustan más jaja',
    time: '15:12',
    verified: true
  }, {
    name: 'Ramiro V.',
    msg: 'Capo el que armó esto. Se nota que sabe de campo, no es un teórico de escritorio. Re recomendado 👌',
    time: '19:40',
    verified: true
  }, {
    name: 'Hernán G.',
    msg: 'Arranqué a tomar laburos más grandes gracias a esto. Antes les tenía miedo a los tableros industriales',
    time: '07:55',
    verified: true
  }, {
    name: 'Leandro M.',
    msg: 'Posta que vale la pena. Mi viejo es electricista y se lo pasé, quedó re manija también',
    time: '12:18',
    verified: false
  }, {
    name: 'Cristian R.',
    msg: 'Lo que más me sirvió fue entender la lógica de los contactores. Antes los conectaba y rezaba jaja',
    time: '21:05',
    verified: true
  }, {
    name: 'Facundo L.',
    msg: 'Che re completo. Los bonos solos ya valen más que lo que pagué. Un golazo la planilla de presupuesto',
    time: '14:50',
    verified: true
  }, {
    name: 'Nicolás S.',
    msg: 'Soy de Córdoba y acá hay mucho laburo industrial. Con esto me animé a meterme en automatización. Gracias crack 🙌',
    time: '16:33',
    verified: true
  }, {
    name: 'Martín H.',
    msg: 'Lo arranqué a leer un domingo y no lo solté. Está explicado como si te lo contara un compañero de laburo',
    time: '11:47',
    verified: true
  }, {
    name: 'Walter B.',
    msg: 'Tengo 15 años en el rubro y hay cosas que no sabía. El tema de protecciones me voló la cabeza',
    time: '09:22',
    verified: true
  }, {
    name: 'Carlos A.',
    msg: 'Muy bueno loco. Lo único que le falta son más ejemplos de variadores, pero para mando es lo mejor que vi',
    time: '18:15',
    verified: false
  }, {
    name: 'Ezequiel D.',
    msg: 'Me lo recomendó un colega y la verdad que tenía razón. Re sirve para el día a día en obra',
    time: '13:08',
    verified: true
  }, {
    name: 'Damián F.',
    msg: 'Che, el bono de paneles solares es tremendo. Estoy pensando en meterme en eso también. Todo suma 🔋',
    time: '20:30',
    verified: true
  }, {
    name: 'Lucas T.',
    msg: 'Posta que cambió mi forma de presupuestar. Antes tiraba números al voleo, ahora tengo todo ordenado',
    time: '15:42',
    verified: true
  }, {
    name: 'Alejandro N.',
    msg: 'Lo mejor es que lo podés consultar en la obra. Nada de andar buscando en YouTube a las apuradas',
    time: '08:10',
    verified: true
  }, {
    name: 'Tomás R.',
    msg: 'Soy de Mendoza, laburo en bodegas. Los circuitos de mando de las bombas ahora los entiendo de verdad',
    time: '17:25',
    verified: true
  }, {
    name: 'Ignacio C.',
    msg: 'Un amigo me dijo "compralo que no te vas a arrepentir" y tenía razón. Muy claro todo',
    time: '12:55',
    verified: false
  }, {
    name: 'Santiago M.',
    msg: 'Re piola el material. Se nota que está hecho por alguien que labura en serio, no un profesor de facultad',
    time: '19:18',
    verified: true
  }, {
    name: 'Agustín V.',
    msg: 'Che lo del protocolo de verificación es genial. Ahora antes de dar tensión reviso todo como corresponde',
    time: '10:05',
    verified: true
  }, {
    name: 'Maximiliano P.',
    msg: 'Increíble la relación precio-calidad. Con lo que sale una herramienta tenés conocimiento para toda la vida',
    time: '14:40',
    verified: true
  }, {
    name: 'Gonzalo E.',
    msg: 'Lo compré medio desconfiado y me sorprendió. Está muy bien armado, se nota el laburo que le metieron 💯',
    time: '16:58',
    verified: true
  }];
  const testimonialData = [{
    name: 'Lucas G.',
    specialty: 'Técnico Electromecánico',
    study: 'Escuela Técnica Raggio',
    comment: 'La claridad de los manuales es impresionante. Poder tener toda la lógica de PLC y mando en un solo lugar me ahorra horas de búsqueda y errores en obra.',
    image: 'https://cdn.magicpatterns.com/uploads/9REeJugtPE4hwPnc79PgSY/Una_foto_fotorrealista_202604171241.webp'
  }, {
    name: 'Juan P.',
    specialty: 'Instalador Eléctrico',
    study: 'Escuela Técnica N°1',
    comment: 'Lo que más me sirvió fue la parte de lógica de mando. Dejé de copiar esquemas y empecé a entender por qué se conecta cada cable.',
    image: 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp'
  }, {
    name: 'Sergio L.',
    specialty: 'Electricista Matriculado',
    study: 'COPIME',
    comment: 'La inversión se recupera en el primer trabajo que hacés con seguridad. Mis clientes notan la diferencia en mi criterio técnico.',
    image: 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp'
  }, {
    name: 'Roberto G.',
    specialty: 'Electricista Industrial',
    study: 'UTN (Univ. Tecnológica Nacional)',
    comment: 'El manual de control eléctrico me cambió la forma de encarar los tableros. Antes dudaba mucho, ahora entiendo la lógica de punta a punta.',
    image: 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp'
  }, {
    name: 'Miguel A.',
    specialty: 'Técnico en Automatización',
    study: 'Centro de Formación Profesional',
    comment: 'Muy recomendado para los que recién arrancan y para los que ya tienen años. Siempre se aprende algo nuevo con estos manuales.',
    image: 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp'
  }, {
    name: 'Carlos M.',
    specialty: 'Mantenimiento de Plantas',
    study: 'Instituto Técnico Superior',
    comment: 'Excelente material. Los diagramas son súper claros y me sirvieron para resolver una falla en un motor que me tenía loco.',
    image: 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp'
  }];
  // Scroll reveal for sections - Defer observer setup
  useEffect(() => {
    const timer = setTimeout(() => {
      const revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-reveal');
            revealObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });
      document.querySelectorAll('[data-reveal]').forEach(el => {
        revealObserver.observe(el);
      });
      // Observer for sticky bottom bar
      const stickyObserver = new IntersectionObserver(([entry]) => {
        setShowStickyBar(entry.isIntersecting || entry.boundingClientRect.top < 0);
      }, {
        threshold: 0.1
      });
      const compraSection = document.getElementById('compra');
      if (compraSection) stickyObserver.observe(compraSection);
      return () => {
        revealObserver.disconnect();
        stickyObserver.disconnect();
      };
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
        setIsManuallyExpanded(false);
      } else {
        setScrollDirection('up');
      }
      lastScrollY = currentScrollY;
      setHeaderVisible(currentScrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.id === 'fears-section') setFearsVisible(true);
            if (entry.target.id === 'benefits-grid') setProblemVisible(true);
            if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
            if (entry.target.id === 'decision-section') setDecisionVisible(true);
            if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
            if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
          }
        });
      }, {
        threshold: 0.15
      });
      const sections = ['fears-section', 'benefits-grid', 'problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
      sections.forEach(id => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
      return () => observer.disconnect();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://maestro-electrico.impultienda.ar/checkout';
  };
  const handleComboPurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  const handleV2PurchaseClick = () => undefined;
  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent('ViewContent', {
              content_name: 'Bonuses Section'
            });
            observer.disconnect();
          }
        });
      }, {
        threshold: 0.5
      });
      const bonusSection = document.getElementById('bonuses');
      if (bonusSection) observer.observe(bonusSection);
      return () => observer.disconnect();
    }, 3000);
    return () => clearTimeout(timer);
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/mm7Hbj6bhqSXE22mqwyUBz/capture_260705_161753.webp',
    caption: 'Anatomía detallada de sistemas de control'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/5Vx44Tzi9mjgCGHkZ8LLij/capture_260705_161746.webp',
    caption: 'Métodos de lectura paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/rerwMLLbH9KWXhkPYegTcE/capture_260705_161816.webp',
    caption: 'Guía completa de motores monofásicos'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/tVBrmaCpZj3RfS9PXNEFxw/capture_260705_161822.webp',
    caption: 'Lógica de arranque y funcionamiento'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/hN9pdqsDUWFExGkqERAXpm/capture_260705_161841.webp',
    caption: 'Protección y rearmado de equipos'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    setLocalReview(review);
  };
  const fearItems = [{
    icon: Flame,
    title: 'Tenés miedo de quemar un motor',
    desc: 'Un error de conexión y el equipo queda inutilizado.'
  }, {
    icon: AlertCircle,
    title: 'Cuando no responde',
    desc: 'Dás tensión… y el circuito no hace lo que debería.'
  }, {
    icon: Brain,
    title: 'La inseguridad te hace quedar mal frente al cliente',
    desc: 'No por el trabajo, sino por cómo lo enfrentás.'
  }, {
    icon: DollarSign,
    title: 'Estás estancado ganando siempre lo mismo',
    desc: 'porque te falta confianza para aceptar trabajos más grandes.'
  }];
  const decisionItems = [{
    icon: CircuitBoard,
    text: 'Criterio claro para interpretar circuitos de mando',
    keyword: 'Criterio'
  }, {
    icon: Cpu,
    text: 'Lógica aplicada a motores y tableros reales',
    keyword: 'Lógica'
  }, {
    icon: Settings,
    text: 'Seguridad al conectar motores y tableros',
    keyword: 'Seguridad'
  }, {
    icon: Target,
    text: 'Confianza para tomar trabajos más grandes',
    keyword: 'Confianza'
  }, {
    icon: TrendingUp,
    text: 'Crecimiento profesional real y sostenido',
    keyword: 'Crecimiento'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20
      }
    }
  };
  return <div className="min-h-screen bg-gray-950 font-['Poppins'] text-gray-100 selection:bg-blue-500/30" data-id="element-99" data-mp="2841fnjyhg61o">
      {notification && !headerVisible && <PurchaseNotification name={notification.name} city={notification.city} pack={notification.pack} isVisible={isVisible} data-id="element-100" data-mp="2vskoq1pcwmut" />}

      {/* Header - Hidden initially, appears on scroll */}
      <header className={`text-white fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerVisible ? 'animate-header-slide bg-[#172554] shadow-md' : 'opacity-0 -translate-y-full pointer-events-none'}`} data-id="element-101" data-mp="39ycw5659sz9g">
        <div className="container mx-auto px-4 flex justify-between items-center py-3" data-id="element-102" data-mp="3jrtpk91hwccl">
          <div className="flex items-center gap-3 overflow-hidden" data-id="element-103" data-mp="3esz7j9bcqrtj">
            <div className="relative flex-shrink-0" data-id="element-104" data-mp="2r237jowqsqb7">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse" data-id="element-105" data-mp="2mzq63cp13ek8">
                <img src="https://cdn.magicpatterns.com/uploads/tA6D5wwQNzXrTuuBrNPdzP/capture_260206_165744.png" alt="Maestro Eléctrico Logo" className="w-full h-full object-cover" loading="eager" decoding="async" width={40} height={40} data-id="element-106" data-mp="3100w6diikp4c" />
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/vFJKXGx8omQxVsJYgcByYb/Diseno_sin_titulo_(7).webp" alt="Verified" loading="eager" decoding="async" width={16} height={16} className="absolute -top-1 -right-1 w-4 h-4 z-10 drop-shadow-sm pt-[2px] pb-[0px]" data-id="element-107" data-mp="gbd3p14a1c8o" />
            </div>

            <div className="relative h-10 flex items-center min-w-[200px]" data-id="element-108" data-mp="1ykjemhmodr12">
              {isVisible && notification ? <div className="flex items-center gap-2 animate-fade-in-up" data-id="element-109" data-mp="2dbsb9aozwcnp">
                  <div className="bg-green-500 p-1.5 rounded-full flex-shrink-0 shadow-lg shadow-green-500/20" data-id="element-110" data-mp="28bxn3ewlu16c">
                    {notification.pack === 'solo' ? <Zap className="w-4 h-4 text-white fill-white" data-id="element-111" data-mp="1flxeb4yzg3y7" /> : <Flame className="w-4 h-4 text-white fill-white" data-id="element-112" data-mp="1fn8phpurcdzc" />}
                  </div>
                  <div className="flex flex-col justify-center" data-id="element-113" data-mp="1tmhx414wdzrp">
                    <p className="text-[11px] md:text-[12px] font-black leading-tight tracking-tight" data-id="element-114" data-mp="1pjlkcr21gxvo">
                      <span className="text-green-400" data-id="element-115" data-mp="350c7c1h1aczi">
                        {notification.name}
                      </span>{' '}
                      de {notification.city}
                    </p>
                    <p className="text-[9px] md:text-[10px] text-blue-100 font-bold uppercase tracking-wider" data-id="element-116" data-mp="38i9mtczmv4ak">
                      compró{' '}
                      <span className="text-white" data-id="element-117" data-mp="3dj78hzjuqggt">
                        {notification.pack === 'solo' ? 'Maestro Eléctrico' : 'Combo Maestro + PLC'}
                      </span>
                    </p>
                  </div>
                </div> : <span className="font-bold tracking-tight text-[14px] text-blue-100/50" data-id="element-118" data-mp="3r9kdpu5czh2g">
                  Inscripciones abiertas
                </span>}
            </div>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded transition-colors text-[14px] flex-shrink-0 pl-[5px] pr-[5px]" data-id="element-119" data-mp="4u1d6zzf512">
            Ver Oferta
          </button>
        </div>
      </header>

      {/* TOP HERO IMAGE BANNER */}
      <div className="w-full leading-none" data-id="element-120" data-mp="e0xv6z6y1bd0"></div>

      {/* HERO SECTION */}
      <section className="relative flex items-center justify-center overflow-hidden py-4 md:py-10 bg-hero-gradient" data-id="element-121" data-mp="iusa7pblt25r">
        <div className="container mx-auto px-4 relative z-10 flex flex-col items-center" data-id="element-122" data-mp="sn3im0y87uda">
          {/* Red Pill Badge */}
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-[9px] md:text-[11px] font-black uppercase tracking-widest mb-3 md:mb-4 shadow-lg shadow-red-600/20 animate-fade-in-up" data-id="element-123" data-mp="jo9qonvubs62">
            <AlertCircle className="w-3 h-3 md:w-4 md:h-4" data-id="element-124" data-mp="9rdfubigytyc" />
            ATENCIÓN: ELECTRICISTAS Y TÉCNICOS
          </div>

          {/* Main Title */}
          <h1 className="text-[28px] md:text-5xl lg:text-6xl font-black mb-3 md:mb-6 leading-[1] md:leading-[1.1] text-center text-white max-w-[95%] md:max-w-5xl mx-auto uppercase tracking-tight" data-id="element-125" data-mp="s9g1wk0l4v">
            Esta misma semana podés pasar de principiante a experto en{' '}
            <span className="text-blue-400" data-id="element-126" data-mp="3n4qdby0vuwb1">comandos eléctricos...</span>
          </h1>

          {/* Blue Highlight Info Box */}
          <div className="bg-blue-500/10 border border-blue-400/20 backdrop-blur-md rounded-xl p-4 md:p-5 mb-4 md:mb-6 max-w-3xl mx-auto text-center shadow-xl" data-id="element-127" data-mp="1xommt92cm67h">
            <p className="text-sm md:text-xl font-medium text-blue-50 leading-snug md:leading-relaxed" data-id="element-128" data-mp="22lmhyoxntojt">
              ...y sumar entre{' '}
              <span className="text-yellow-400 font-bold" data-id="element-129" data-mp="2bnvpd5xmt1sz">
                US$ 200 y US$ 500
              </span>{' '}
              a tus ganancias mensuales aceptando trabajos que hoy estás
              rechazando con una guía práctica de comandos eléctricos
              industriales.
            </p>
          </div>

          {/* Book Fan - Optimized Structure */}
          <div className="mb-4 md:mb-6 flex justify-center relative w-full items-center h-[240px] md:h-[430px]" onMouseEnter={() => setIsHeroFanRevealed(true)} onMouseLeave={() => setIsHeroFanRevealed(false)} onTouchStart={() => setIsHeroFanRevealed(!isHeroFanRevealed)} data-id="element-130" data-mp="1nwgkp6idu8ek">
            {/* Lightweight Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-600/20 rounded-full blur-[60px] pointer-events-none" data-id="element-131" data-mp="106m9etdporwf" />

            {/* Bonus books (Loaded only when ready) */}
            {isReadyForBonuses && heroBonusBooks.map(book => <motion.img key={book.position} src={book.src} alt={book.alt} loading="lazy" decoding="async" width={280} height={373} className="absolute w-[220px] md:w-[280px] h-auto object-contain select-none pointer-events-none" initial="hidden" animate={isHeroFanRevealed ? 'visible' : 'hidden'} variants={fanVariants} custom={book.position} style={{
            zIndex: heroPositionConfig[book.position].z,
            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.4))'
          }} data-id="element-132" data-mp="qvhsftcd3jz0" />)}

            {/* Main Book - LCP Priority */}
            <img src="https://cdn.magicpatterns.com/uploads/fMZ5Fq5SNbxYwzfww2mdPS/Diseno_sin_titulo_(1).webp" alt="Control Eléctrico Libro Digital" className="relative w-full max-w-[240px] md:max-w-[340px] h-auto object-contain select-none cursor-pointer z-20" onClick={() => setIsHeroFanRevealed(!isHeroFanRevealed)} decoding="async" fetchPriority="high" loading="eager" width={340} height={453} style={{
            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))',
            transform: isHeroFanRevealed ? 'scale(0.95) translateY(-5px)' : 'none',
            transition: 'transform 0.4s ease-out'
          }} data-id="element-133" data-mp="3bk4etu03gv9o" />

            {/* Subtle label */}
            <p className={`absolute bottom-0 left-1/2 -translate-x-1/2 text-[11px] md:text-xs text-blue-300/60 font-medium uppercase tracking-widest whitespace-nowrap pointer-events-none transition-opacity duration-300 ${isHeroFanRevealed ? 'opacity-100' : 'opacity-0'}`} data-id="element-134" data-mp="13b7ril7pu539">
              1 Manual Principal + 5 Bonos Técnicos
            </p>

            {/* Interaction hint */}
            {!isHeroFanRevealed && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-blue-400/40 text-[10px] font-medium uppercase tracking-widest animate-pulse" data-id="element-135" data-mp="2ntfdfllmwwl1">
                Tocá el libro para ver los bonos
              </div>}
          </div>

          {/* Stars + Social proof */}
          <div className="text-center mb-6 md:mb-8" data-id="element-136" data-mp="2ma5lx09x0sqh">
            <div className="inline-flex flex-col items-center p-4 md:p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-137" data-mp="2hbud8svcwo9u">
              <p className="text-sm md:text-base font-semibold text-blue-100/80 mb-3" data-id="element-138" data-mp="2ceu7kj2f5nj9">
                Del cableado básico al control industrial: Dominá el sistema
                completo.
              </p>
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-139" data-mp="352qai5il0boh">
                <div className="flex items-center gap-1" data-id="element-140" data-mp="309x6jjuxt7dg">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]" data-id="element-141" data-mp="2vcwxr16pdqin" />)}
                </div>
                <span className="text-sm font-bold text-yellow-400" data-id="element-142" data-mp="3stdlx0rphjfs">
                  4.9/5 Calificación
                </span>
              </div>
              <p className="text-xs md:text-sm font-bold text-blue-400 uppercase tracking-[0.15em]" data-id="element-143" data-mp="4u8ayv45lgw">
                8.264 profesionales verificados ya compraron en Argentina
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-2" data-id="element-144" data-mp="973jlqy7hivc">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-3xl font-black py-5 px-12 rounded-xl shadow-[0_20px_50px_rgba(234,179,8,0.3)] transition-all inline-flex items-center gap-3 uppercase tracking-tight" data-id="element-145" data-mp="e4vbikyup4zj">
              QUIERO EMPEZAR AHORA
              <ArrowRight className="w-6 h-6 md:w-8 md:h-8" strokeWidth={3} data-id="element-146" data-mp="rvs3qaov7k62" />
            </button>
            <div className="mt-5 grid w-full max-w-3xl grid-cols-2 divide-x divide-blue-300/15 rounded-xl border border-blue-400/25 bg-blue-500/10 text-left text-[8px] font-bold uppercase tracking-[0.08em] text-blue-100/90 md:text-sm md:tracking-widest" data-id="element-147" data-mp="wttkuq2qd9ew">
              <div className="min-w-0 px-2.5 py-3 md:px-4" data-id="element-148" data-mp="1s37ns59x48um">
                <p className="mb-2 text-[8px] font-black tracking-[0.1em] text-blue-300 md:text-[9px] md:tracking-[0.16em]" data-id="element-149" data-mp="1s5kcd1a6lhgn">TU ACCESO INCLUYE</p>
                <div className="space-y-1.5" data-id="element-150" data-mp="214nvg59clwv5">
                  <span className="flex items-start gap-1" data-id="element-151" data-mp="213cra4bzqn3i"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-152" data-mp="1w5az8twl7dfw" />Descarga inmediata</span>
                  <span className="flex items-start gap-1" data-id="element-153" data-mp="18g9ylstzx6l6"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-154" data-mp="18d4603m5rmkp" />Acceso permanente</span>
                  <span className="flex items-start gap-1" data-id="element-155" data-mp="1i5jor35a4f6n"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-156" data-mp="1i6l2jfdojfs2" />Sin mensualidades</span>
                  <span className="flex items-start gap-1" data-id="element-157" data-mp="popgjkrhogm0"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-158" data-mp="pno53f6k9s3r" />Actualizaciones incluidas</span>
                </div>
              </div>
              <div className="min-w-0 px-2.5 py-3 md:px-4" data-id="element-159" data-mp="2wcznn9o85uga">
                <p className="mb-2 text-[8px] font-black tracking-[0.1em] text-blue-300 md:text-[9px] md:tracking-[0.16em]" data-id="element-160" data-mp="313y2s5xh8mhy">PENSADO PARA ARGENTINA</p>
                <div className="space-y-1.5" data-id="element-161" data-mp="2ndumdvla3n0f">
                  <span className="flex items-start gap-1" data-id="element-162" data-mp="2sbnrsmercczn"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-163" data-mp="2scpfprgvgtys" />Referencias AEA 90364 · IRAM</span>
                  <span className="flex items-start gap-1" data-id="element-164" data-mp="3fze1wpcwz8l7"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-165" data-mp="3k33oj9a13g3l" />Terminología técnica en español</span>
                  <span className="flex items-start gap-1" data-id="element-166" data-mp="3655ciaj6jvr9"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500 md:h-4 md:w-4" strokeWidth={3} data-id="element-167" data-mp="3b1m9lu8cxkgh" />Criterios para el trabajo local</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POST-HERO - Antes vs Después Comparison */}
      <section id="post-hero-section" className="py-6 px-4" style={{
      background: '#0a1929'
    }} data-id="element-168" data-mp="284m50z3536po">
        <div className="container mx-auto max-w-2xl" data-id="element-169" data-mp="2d24dyph5plxo">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="relative bg-[#0a1a3a] border-2 border-yellow-500/80 rounded-3xl p-4 md:p-6 shadow-2xl overflow-hidden pt-[10px] pb-[10px] pl-[5px] pr-[5px]" data-id="element-170" data-mp="2d4qpf0764ll6">
            {/* Comparison Table */}
            <div className="grid grid-cols-2 mb-4 pl-[10px] pr-[10px] gap-[5px]" data-id="element-171" data-mp="2428zobsb1atf">
              {/* Header */}
              <div className="bg-[#3d1a33] border border-red-500/30 p-2.5 text-center rounded-xl flex items-center justify-center" data-id="element-172" data-mp="16n3guggeaxpi">
                <p className="text-[11px] md:text-xs font-black text-pink-300 uppercase tracking-tighter leading-tight" data-id="element-173" data-mp="1bkwq5iczw7u0">
                  HOY <br data-id="element-174" data-mp="3ovetr3mtkyfs" /> TE PASA ESTO
                </p>
              </div>
              <div className="bg-[#1a2b4a] border-2 border-yellow-500/50 p-2.5 text-center rounded-xl flex items-center justify-center" data-id="element-175" data-mp="3kvxq8fqyebd9">
                <p className="text-[11px] md:text-xs font-black text-yellow-400 uppercase tracking-tighter leading-tight" data-id="element-176" data-mp="3fxcogbw1zjmp">
                  CON MAESTRO ELÉCTRICO
                </p>
              </div>

              {/* Rows */}
              {[{
              left: 'Mirás un diagrama y no sabés por dónde empezar',
              right: 'Entendés la lógica detrás de cada circuito de mando'
            }, {
              left: 'Conectás componentes, pero no entendés el circuito completo',
              right: 'Interpretás diagramas sin depender de nadie'
            }, {
              left: 'Cuando aparece un tablero, dudás o evitás el trabajo',
              right: 'Armás tableros con criterio técnico'
            }, {
              left: 'Cobras lo mismo hace años',
              right: 'Sabés qué estás haciendo en cada conexión'
            }, {
              left: 'Dependés de otro para validar si está bien',
              right: 'Empezás a encarar trabajos más técnicos y mejor pagos'
            }].map((row, i) => <Fragment key={i} data-id="element-177" data-mp="23ksqu101yrp8" data-mpi={i}>
                  <motion.div initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-2 py-1.5 border-b border-gray-700/30" data-id="element-178" data-mp="23m3xq9osofkk">
                    <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-179" data-mp="2mj51xsaiybng" />
                    <p className="text-[11px] md:text-sm text-gray-300 leading-tight font-medium" data-id="element-180" data-mp="3a9r8tg8slpvv">
                      {row.left}
                    </p>
                  </motion.div>
                  <motion.div initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-2 py-1.5 border-b border-gray-700/30 pt-[10px] pb-[10px] pl-[15px] pr-[15px]" data-id="element-181" data-mp="13l8sqaj21qvo">
                    <Check className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-182" data-mp="kluu3fadrryu" />
                    <p className="text-[11px] md:text-sm text-white leading-tight font-bold" data-id="element-183" data-mp="fnjjv3g6bfg3">
                      {row.right}
                    </p>
                  </motion.div>
                </Fragment>)}
            </div>

            {/* Closing statement */}
            <div className="text-center flex flex-col items-center pt-2 gap-[0px]" data-id="element-184" data-mp="pk64ps8i26i4">
              <Zap className="w-6 h-6 text-yellow-400 fill-yellow-400 animate-pulse-glow" data-id="element-185" data-mp="25y60cr0bb9zk" />
              <p className="text-sm md:text-base font-black text-yellow-400 leading-tight uppercase tracking-tight" data-id="element-186" data-mp="20zusgltkv9jz">
                UN ELECTRICISTA INDUSTRIAL RESUELVE EL CIRCUITO ANTES DE
                TOCARLO.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - With animated background */}
      <section id="problem-real-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-187" data-mp="oj213zlv5lsq">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-188" data-mp="yh80oak76fuf"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-189" data-mp="139hnvitxh1t0">
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="text-3xl md:text-4xl font-extrabold text-white mb-10 text-center uppercase tracking-wide" data-id="element-190" data-mp="17abbsc7deglw">
            EL <span className="text-yellow-400" data-id="element-191" data-mp="1mca0kvyvacxp">PROBLEMA</span> REAL
          </motion.h3>

          {/* Problem Cards - Compact with images */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="flex flex-col items-center gap-4 mb-10" data-id="element-192" data-mp="1he8es3a23zij">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/kTTYbddcoAFGwaZadg6Lz9/imagine_prompt__A_202604230935.webp',
            text: 'Ves un esquema de lógica industrial y no sabés por dónde empezar. Conectás de memoria, rezás para que no vuele nada y, si algo falla,',
            redLine: 'no tenés idea de por qué.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/jPXCfpqddnTsx86wJZwdCV/_imagine_prompt__Realistic_202604230937.webp',
            text: 'Te llaman para automatizar un tablero o configurar un contactor y tenés que decir que no.',
            redLine: 'Acabás de regalarle ese cliente (y esa plata) a otro que sí sabe.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/5YfikYVmg6p1jfmJoyEb7c/_imagine_prompt__A_202604230937.webp',
            text: 'Dependés de que un tercero valide lo que hacés. Sin la lógica clara, quedás expuesto ante el cliente y',
            redLine: 'perdés la autoridad como profesional.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/xhRSvJF7qdoXAjziMfNMU9/_imagine_prompt__Realistic_202604230937_(1).webp',
            text: 'Seguís cobrando lo mismo de hace años haciendo instalaciones básicas. Sin dominar el control industrial, tu techo ya está puesto y',
            redLine: 'estás dejando pasar los trabajos que realmente valen.'
          }].map((card, i) => <motion.div key={i} variants={itemVariants} whileHover={{
            scale: 1.02
          }} className="w-full max-w-[600px] bg-white rounded-xl overflow-hidden shadow-lg transition-all duration-300" data-id="element-193" data-mp="1nv9zdly5pviv" data-mpi={i}>
                <div className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] min-h-[120px]" data-id="element-194" data-mp="3dbngo0pk8wt6">
                  <div className="aspect-[3/4] overflow-hidden" data-id="element-195" data-mp="38du8x38lzuqs">
                    <img src={card.image} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" width={140} height={187} data-id="element-196" data-mp="3n170p9p0ewpm" />
                  </div>
                  <div className="p-3 md:p-4 flex flex-col justify-center gap-1" data-id="element-197" data-mp="3w1lnkdhmgjv8">
                    <p className="text-gray-700 text-xs md:text-sm leading-snug font-[500]" data-id="element-198" data-mp="3w3g6xnd8w3x7">
                      {card.text}
                    </p>
                    <p className="text-red-600 text-xs md:text-sm font-bold leading-snug" data-id="element-199" data-mp="9tc9veh2m4l6">
                      {card.redLine}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ¿TE PASA ESTO? - With animated background */}
      <section id="fears-section" className="py-8 px-4 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 animate-gradient-slow relative overflow-hidden" data-id="element-200" data-mp="26oi1tqs860ps">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-201" data-mp="1prt6qdsm2t50"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-202" data-mp="1ule546y49p4q">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-203" data-mp="1zh2vpks5rvow">
            <h3 className="md:text-5xl font-extrabold text-white uppercase text-[32px] mb-[0px]" data-id="element-204" data-mp="16tyza474spoq">
              ¿TE <span className="text-yellow-400" data-id="element-205" data-mp="1bricd0s3ewf3">PASA</span> ESTO?
            </h3>
            <p className="text-gray-400 text-[18px] pb-[20px] mt-[0px] mb-[0px]" style={{
            paddingBottom: "0px"
          }} data-id="element-206" data-mp="1gso8z9ovj3tw">
              Si trabajás con tableros, motores y circuitos, esto es para vos.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20,
          delay: 0.2
        }} className="problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg pt-[10px] pb-[10px] pr-[24px] space-y-[1px]" data-id="element-207" data-mp="2s8wbjb4b2f5k">
            <div className="space-y-6" data-id="element-208" data-mp="3t2oa6zfqpfyw">
              {fearItems.map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4 + i * 0.1
            }} className="flex items-start gap-4 pb-6 border-b border-gray-700 last:border-0 pb-[10px] mt-[10px] gap-[16px] items-center justify-center" data-id="element-209" data-mp="3oz9te6jz8vke" data-mpi={i}>
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-210" data-mp="1das0rd8imk5f">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-211" data-mp="255w56oq7hk40" />
                  </div>
                  <div data-id="element-212" data-mp="2071optfpo7n2">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-213" data-mp="1vdytzhz76s5a">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-214" data-mp="1qe43vgn8y6la">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <div className="border-t border-gray-700 text-center mt-[30px] pt-[35px] pb-[35px]" data-id="element-215" data-mp="1mevawe016cqs">
              <p className="text-xl font-medium text-gray-300 mb-2" data-id="element-216" data-mp="1hd5xwst8zp02">
                Ahí aparece la inseguridad.
              </p>
              <p className="text-xl font-bold text-white" data-id="element-217" data-mp="17gb24hod02ph">
                Y en control eléctrico, la inseguridad{' '}
                <span className="text-yellow-400" data-id="element-218" data-mp="36tx5rtlrfsa7">se paga caro</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS GRID SECTION - COMPRESSED */}
      <section id="benefits-grid" className="py-7 md:py-10 bg-white relative overflow-hidden" data-id="element-219" data-mp="3ac4hhafzzmze">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.03]" data-id="element-220" data-mp="3ec4ucqng7q4c"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-221" data-mp="3j4erorqw7sgn">
          <div className="text-center mb-5 md:mb-6" data-id="element-222" data-mp="3o2zik0k98zsf">
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 uppercase tracking-tight" data-id="element-223" data-mp="3o4kd3q6kkiqa">
              Con esta guía <span className="text-blue-600" data-id="element-224" data-mp="2ve28gkq769ar">vas a poder:</span>
            </h2>
            <div className="w-12 h-1 bg-blue-600 mx-auto mt-2.5 rounded-full" data-id="element-225" data-mp="2vfvk6efo3jpm"></div>
          </div>

          <div className="grid grid-cols-2 grid-rows-4 gap-2.5 max-w-3xl mx-auto" style={{
          gridAutoRows: '1fr'
        }} data-id="element-226" data-mp="30fhx0mrl2xxw">
            {[{
            title: 'Leer e interpretar cualquier diagrama',
            desc: 'Aprenderás cada símbolo y conexión con ilustraciones 100% visuales.',
            icon: Ruler,
            highlight: true
          }, {
            title: 'Instalar 20 circuitos paso a paso',
            desc: '9 monofásicos + 11 trifásicos: arranque directo, inversión, estrella-triángulo y más.',
            icon: Zap
          }, {
            title: 'Diagnosticar las 16 fallas más comunes',
            desc: 'Capítulo exclusivo con causa y solución paso a paso para fallas reales.',
            icon: Search
          }, {
            title: 'Trabajar con seguridad total — LOTO',
            desc: 'Procedimientos exactos (OSHA/NOM) para intervenir equipos sin riesgo.',
            icon: Lock
          }, {
            title: 'Dimensionar sin calcular de memoria',
            desc: 'Tablas listas: HP → FLA → cable → protección. Selecciona en segundos.',
            icon: BarChart3
          }, {
            title: 'Saber cuánto cobrar sin dudar',
            desc: 'Tabla de presupuesto incluida para calcular precios de forma rápida y segura.',
            icon: Banknote
          }, {
            title: 'Dominar NEC, NOM e IEC',
            desc: 'Diferencias entre normas para trabajar en cualquier país con idioma técnico.',
            icon: Globe,
            isNew: true,
            highlight: true
          }, {
            title: 'Glosario español-inglés',
            desc: 'Más de 200 términos técnicos. Ideal para trabajar con equipos importados.',
            icon: BookOpen,
            isNew: true,
            highlight: true
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className={`relative flex h-full min-w-0 flex-col rounded-xl border-2 bg-white p-3 shadow-sm ${item.highlight ? 'border-blue-500/30' : 'border-gray-100'}`} data-id="element-227" data-mp="1itjpjk9k6u3j" data-mpi={i}>
                {item.isNew && <div className="absolute -top-2 left-2 bg-blue-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md z-10" data-id="element-228" data-mp="2hwaovbrpuxk">
                    ★ NOVEDAD
                  </div>}

                <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center mb-2" data-id="element-229" data-mp="dx0wui1p90hl">
                  <item.icon className="w-4 h-4 text-blue-600" data-id="element-230" data-mp="8zhjrlgqmtr8" />
                </div>

                <h3 className="text-[13px] font-black text-gray-900 mb-1.5 leading-tight" data-id="element-231" data-mp="40o2dhybyils">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-snug font-medium text-[12px] break-words" data-id="element-232" data-mp="11lalrsu7m4cy">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="bg-white" data-id="element-233" data-mp="nodnj6brhkm1">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="container mx-auto max-w-4xl px-4 py-16 text-center bg-[#091D47] pt-[15px] pb-[15px] pl-[16px] pr-[16px]" data-id="element-234" data-mp="3cf6ebure6iao">
          <motion.span initial={{
          y: 10,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-yellow-400 text-blue-900 font-black px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-6 inline-block shadow-sm" data-id="element-235" data-mp="1nxn2l0mlf0f0">
            LA SOLUCIÓN DEFINITIVA
          </motion.span>
          <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 uppercase tracking-tight" data-id="element-236" data-mp="26ls7ibezkrfe">
            <span className="text-white" data-id="element-237" data-mp="2kjp3p6pj2vtj">AHÍ NACE </span>
            <span className="text-yellow-400" data-id="element-238" data-mp="la0dq0vxr5lb">MAESTRO ELÉCTRICO</span>
          </motion.h2>
          <motion.p initial={{
          y: 20,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-xl text-gray-400 font-medium" data-id="element-239" data-mp="q93a6wxuo3ho">
            Un manual práctico creado para ayudarte a:
          </motion.p>
        </motion.div>

        {/* Two-Column Block */}
        <div className="bg-[#091D47] py-20 pt-[20px] pb-[20px]" data-id="element-240" data-mp="1xjrlzrizrzv5">
          <div className="container mx-auto max-w-6xl px-4" data-id="element-241" data-mp="2chjwg59oly2h">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-242" data-mp="2cfpdgwhz0g11">
              {/* Left Column - Text */}
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} className="w-full space-y-3" data-id="element-243" data-mp="2la3zrj0x4qtn">
                {['Entender la lógica de los circuitos de mando.', 'Leer diagramas con seguridad.', 'Aplicar lo aprendido en motores y tableros reales.', 'Dejar de memorizar esquemas y empezar a comprenderlos.'].map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3 p-3 rounded-lg" data-id="element-244" data-mp="39v0enz9t9dzx" data-mpi={index}>
                    <div className="bg-yellow-400 p-1 rounded-full flex-shrink-0 mt-0.5" data-id="element-245" data-mp="3eumri7lq8s87">
                      <Check className="w-3.5 h-3.5 text-blue-900 stroke-[3px]" data-id="element-246" data-mp="2zx2qfbrybh7b" />
                    </div>
                    <p className="text-blue-100 font-medium text-base leading-snug" data-id="element-247" data-mp="34ve11oq2lvql">
                      {item}
                    </p>
                  </motion.div>)}
              </motion.div>

              {/* Right Column - Horizontal Carousel */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 100,
              damping: 20,
              delay: 0.4
            }} className="w-full" data-id="element-248" data-mp="2vwjvv9yobx1f">
                <HorizontalCarousel images={['https://cdn.magicpatterns.com/uploads/9REeJugtPE4hwPnc79PgSY/Una_foto_fotorrealista_202604171241.webp', 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp', 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp', 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp', 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp', 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp']} data-id="element-249" data-mp="2c6girlluzyxu" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* DECISION BLOCK */}
      <section className="py-8 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden" data-id="element-250" data-mp="1q0fdc8g2c8pv">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" data-id="element-251" data-mp="1lzlptg6j8vxi"></div>

        {/* Professional Work Headline + Marquee */}
        <div className="relative z-10 pt-[0px] pb-[32px]" data-id="element-252" data-mp="5gtuar48tv2c">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="md:text-5xl font-extrabold text-center uppercase px-4 text-[18px] pl-[16px] pr-[16px] mb-[15px]" data-id="element-253" data-mp="agy98cpi6hqg">
            LOS <span className="text-[#FFD700]" data-id="element-254" data-mp="3iwriq7lf2sy0">TRABAJOS PROFESIONALES</span>{' '}
            QUE VAS A PODER LOGRAR
          </motion.h2>

          {/* Seamless Marquee */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} className="w-full overflow-hidden" style={{
          maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
        }} data-id="element-255" data-mp="3ntrmucyphlmh">
            <div className="work-marquee-track flex" style={{
            gap: 0
          }} data-id="element-256" data-mp="2euec4rrncbm0">
              {[...Array(2)].map((_, setIdx) => <div key={setIdx} className="flex" style={{
              gap: 0
            }} data-id="element-257" data-mp="20veq7rviqatj" data-mpi={setIdx}>
                  {['https://cdn.magicpatterns.com/uploads/7HGnWVYdGUounHGPTR5os9/ANXO_Cidery2-884x936.webp', 'https://cdn.magicpatterns.com/uploads/1dL5zYkw5FFehBYAgx2tnp/IMG_20220326_113419434_HDR.webp', 'https://cdn.magicpatterns.com/uploads/9QDkoYsbeL9saJPGxUxeGM/l.webp', 'https://cdn.magicpatterns.com/uploads/c2yC3nXaAGLmMwdQdVXgAk/4adca5c332d9c89ba2eb667fbec01413.webp', 'https://cdn.magicpatterns.com/uploads/w3LHvz1VbrGMohvvkY3fc8/02_(1).webp', 'https://cdn.magicpatterns.com/uploads/ov65eS7vV5W5DJn5gyZ4Gi/capture_260423_102902.webp', 'https://cdn.magicpatterns.com/uploads/47tYGnACbZUj4Jiak4CcvX/Electrician_smiling_at_202604231027.webp', 'https://cdn.magicpatterns.com/uploads/hPrxHuZ14satPewQLK9Jus/Electrician_using_multimeter_202604231026_(1).webp', 'https://cdn.magicpatterns.com/uploads/wjbtDSssEcZSkMbHzQHbDQ/650301318_865258023230524_1857744393700602518_n.webp', 'https://cdn.magicpatterns.com/uploads/jm6cXtUVxQEbLdjG1CoaRA/descargar.webp'].map((src, i) => <div key={`${setIdx}-${i}`} className="flex-shrink-0" style={{
                width: '220px',
                aspectRatio: '3/4'
              }} data-id="element-258" data-mp="yj55t1ljyqb8" data-mpi={i}>
                      <img src={src} alt={`Trabajo profesional ${i + 1}`} className="w-full h-full object-cover" loading={i < 3 ? 'eager' : 'lazy'} decoding="async" width={220} height={293} data-id="element-259" data-mp="17k320xpr1tj9" />
                    </div>)}
                </div>)}
            </div>
          </motion.div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-260" data-mp="1hc8sg30cs2wc">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-261" data-mp="1va724v3qbb5i">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-3" data-id="element-262" data-mp="1qauew98y422e">
              {decisionItems.map((item, index) => <motion.div key={index} variants={itemVariants} whileHover={{
              scale: 1.02,
              x: 5
            }} className="flex items-center gap-4 p-4 rounded-lg bg-blue-800/40 border border-blue-600/40 transition-all duration-300 hover:bg-blue-700/50 hover:border-blue-500/50 hover:shadow-lg" data-id="element-263" data-mp="svq1q8tr5ihd" data-mpi={index}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="element-264" data-mp="16ml3l15u2zbg">
                    <item.icon className="w-5 h-5 text-yellow-400" data-id="element-265" data-mp="1blxzscilhiqp" />
                  </div>
                  <span className="text-lg text-gray-100" data-id="element-266" data-mp="1glkden2c5101">
                    {item.text.split(item.keyword).map((part, i, arr) => <span key={i} data-id="element-267" data-mp="1kme2vkvfenv1" data-mpi={i}>
                        {part}
                        {i < arr.length - 1 && <span className="text-blue-300" data-id="element-268" data-mp="3idyujjx221fe">{item.keyword}</span>}
                      </span>)}
                  </span>
                </motion.div>)}
            </motion.div>
          </div>

          <div className="text-center" data-id="element-269" data-mp="3evj4e6r1qtcw">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-2xl font-extrabold py-5 px-12 rounded-lg shadow-2xl hover:shadow-yellow-500/30 transition-all" data-id="element-270" data-mp="3ewklosynoc32">
              SÍ, QUIERO LA OFERTA AHORA
            </motion.button>
          </div>
        </div>
      </section>

      {/* BONUSES - Carousel */}
      <section id="bonuses" className="py-8 px-4 bg-[#00005E]" style={{
      paddingBottom: "4px"
    }} data-id="element-271" data-mp="3sw3nl40vir6x">
        <div className="container mx-auto max-w-6xl" data-id="element-272" data-mp="3ose0yk3rejoj">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-273" data-mp="3juck85tt2wg8">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight relative inline-block" data-id="element-274" data-mp="2dbxliqs7g31o">
              SI ACCEDÉS HOY, TE LLEVÁS ESTOS 5 BONUS DE REGALO
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" data-id="element-275" data-mp="56c9dli5mqg"></span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 mt-4" data-id="element-276" data-mp="3ncrxzblwyvvi">
              Complementos perfectos para tu crecimiento profesional
            </p>

            <BookStackFan data-id="element-277" data-mp="iwh3jvv541iv" />

            {/* Swipe Instruction - High Visibility for older users */}
            <div className="mb-6 flex flex-col items-center gap-3" data-id="element-278" data-mp="nwlivikbaq7i">
              <motion.p animate={{
              x: [0, 5, 0]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="text-yellow-400 font-black text-sm md:text-base tracking-widest uppercase flex items-center gap-2" data-id="element-279" data-mp="ntplbvcdm3jl">
                DESLIZÁ PARA VER TODOS LOS BONOS INCLUIDOS
                <ArrowRight className="w-5 h-5" data-id="element-280" data-mp="2zmnt70wzoq2x" />
              </motion.p>

              {/* Animated Swipe Indicator */}
              <div className="flex items-center gap-4 text-gray-500 md:hidden" data-id="element-281" data-mp="2r31145bird1o">
                <motion.div animate={{
                x: [-10, 10, -10],
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-282" data-mp="2n1z2nqpff50r">
                  <ChevronLeft className="w-6 h-6" data-id="element-283" data-mp="2w5059hf32dzo" />
                </motion.div>
                <div className="w-12 h-1 bg-gray-800 rounded-full overflow-hidden" data-id="element-284" data-mp="28dcmjmrz698h">
                  <motion.div animate={{
                  x: [-48, 48, -48]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="w-full h-full bg-yellow-500/50" data-id="element-285" data-mp="23fazkqrfhprq" />
                </div>
                <motion.div animate={{
                x: [10, -10, 10],
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-286" data-mp="2dbmhpxdp99u3">
                  <ChevronRight className="w-6 h-6" data-id="element-287" data-mp="1ko0e4p4h2578" />
                </motion.div>
              </div>
            </div>

            {/* Snap-Scroll Carousel */}
            <div className="relative group" data-id="element-288" data-mp="3ppcin39rgvjw">
              <div ref={bonusScrollRef} onScroll={handleBonusScroll} className="flex gap-4 md:gap-8 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-8 px-4 md:px-0" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-289" data-mp="3po1gf7vyrvuw">
                {[{
                title: 'CURSO DE PANELES SOLARES',
                desc: 'Más de 50 clases grabadas para sumar una nueva especialidad altamente demandada en energía solar.',
                img: 'https://cdn.magicpatterns.com/uploads/w2rTPZ1b9h5D7JWz5dRRda/usa-la-imagen-adjunta-como-referencia-vi_LIZOyjwkQ0eu3ZbUzqbGFw_459-daMfTbW7XMmyjjHVYA_2k.png',
                value: '$32.500 ARS',
                exclusive: true,
                label: 'BONO EXCLUSIVO'
              }, {
                title: 'PLANTILLAS DE PRESUPUESTO EDITABLE',
                desc: 'Herramienta editable diseñada para trabajos eléctricos profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/52EEtPjWC34BYh7Nk8wekR/Prompt__A_high-end,_202604151035.webp',
                value: '$6.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'PROTOCOLO DE VERIFICACIÓN PREVIA A LA ENERGIZACIÓN',
                desc: 'Documento técnico estructurado que establece criterios de verificación.',
                img: 'https://cdn.magicpatterns.com/uploads/ecDoD4jMmyVG3yjoo5c8Tn/Prompt__A_detailed,_202604151040.webp',
                value: '$9.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'EFICIENCIA EN USO INDUSTRIAL DE MOTORES ELÉCTRICOS',
                desc: 'Material técnico orientado al uso correcto de motores eléctricos en entornos industriales.',
                img: 'https://cdn.magicpatterns.com/uploads/nH726tHAQweq2rPqiPh3zm/A_detailed,_premium_202604151030.webp',
                value: '$14.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'REGLAMENTACIONES AEA - ASOCIACIÓN ELECTROTÉCNICA ARGENTINA',
                desc: 'Manual esencial sobre las normativas vigentes de la AEA para instalaciones eléctricas seguras y profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/xz24GCFVSGY7FVuNDfknEw/necesito_hacer_el_202604151258.jpg',
                value: '$12.500 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }].map((bonus, i) => <div key={i} className="flex-shrink-0 w-[85%] md:w-[320px] snap-center md:snap-start" data-id="element-290" data-mp="27crxo9wepcz8" data-mpi={i}>
                    <motion.div whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={scrollToPurchase} className="cursor-pointer h-full" data-id="element-291" data-mp="1tds7uyx5qs1h">
                      <div className={`bg-gray-950 rounded-xl overflow-hidden border-2 h-full flex flex-col ${bonus.exclusive ? 'border-yellow-400 shadow-xl shadow-yellow-500/10' : 'border-yellow-600/50 shadow-lg'}`} data-id="element-292" data-mp="3nqqhdgb19knr">
                        <div className={`${bonus.exclusive ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' : 'bg-gradient-to-r from-yellow-700 to-yellow-600 text-black'} px-4 py-2 text-center font-bold text-sm uppercase`} data-id="element-293" data-mp="6pyngmkqm5go">
                          {bonus.label}
                        </div>
                        <div className="p-5 flex flex-col flex-grow" data-id="element-294" data-mp="appbiiivq7hi">
                          <div className="aspect-[3/4] mb-4 bg-black rounded-lg overflow-hidden relative" data-id="element-295" data-mp="peviisnyl1zy">
                            {bonus.exclusive && <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded z-10 tracking-wider" data-id="element-296" data-mp="kfinvlquiqmt">
                                BONO PREMIUM
                              </div>}
                            <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={300} height={400} data-id="element-297" data-mp="307g990b9ai97" />
                          </div>
                          <h3 className={`font-bold text-sm ${bonus.exclusive ? 'text-yellow-400' : 'text-white'} mb-3 leading-tight uppercase`} data-id="element-298" data-mp="2w87gnyrycqf8">
                            {bonus.title}
                          </h3>
                          <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow" data-id="element-299" data-mp="3laovz5xrhjj9">
                            {bonus.desc}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-300" data-mp="3gi5hm6r92kde">
                            Valor:{' '}
                            <span className={`line-through ${bonus.exclusive ? 'text-red-400 font-medium' : 'text-gray-500'}`} data-id="element-301" data-mp="3vaz9ysrmeuzh">
                              {bonus.value}
                            </span>
                          </p>
                          <div className={`${bonus.exclusive ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg px-3 py-2 mt-auto`} data-id="element-302" data-mp="353qqdi0i95n">
                            <p className={`text-sm font-bold ${bonus.exclusive ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-303" data-mp="24wynzo3iub4c">
                              ✓ Incluido sin costo adicional
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>)}
              </div>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-2 mt-2" data-id="element-304" data-mp="fn651arqoqj9">
                {[...Array(5)].map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeBonusIndex ? 'bg-yellow-500 w-6' : 'bg-gray-700 w-1.5'}`} data-id="element-305" data-mp="1nn87e4dp1m9" data-mpi={i} />)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EXTRA BONUS - CERTIFICATE */}
      <section className="py-8 px-4 bg-gradient-to-b from-[#0a1a3a] to-[#061229] relative overflow-hidden" data-id="element-306" data-mp="3su7y2uip63hp">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" style={{
        paddingBottom: "9px"
      }} data-id="element-307" data-mp="1n0zoqs3i523n"></div>
        <div className="container mx-auto max-w-2xl relative z-10" data-id="element-308" data-mp="3001lh8hw9qjc">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="flex flex-col items-center text-center" data-id="element-309" data-mp="301cpn9f950az">
            {/* Certificate Image at the top with frame */}
            <motion.div whileHover={{
            scale: 1.05,
            rotate: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} className="relative group w-full max-w-[320px] mb-6" data-id="element-310" data-mp="3iprepr60e495">
              <div className="absolute -inset-1 bg-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-311" data-mp="34zxqnb2bvo0h"></div>
              <div className="relative bg-[#1a2b4a] border-[6px] border-[#2a3b5a] p-2 md:p-4 rounded-xl shadow-2xl overflow-hidden" data-id="element-312" data-mp="6ayyc64szm7i">
                <img src="https://cdn.magicpatterns.com/uploads/wgzBvYCj2MeubudywG2qNn/Diseno_sin_titulo_(2).png" alt="Certificado Maestro Eléctrico" className="w-full h-auto rounded-sm" loading="lazy" decoding="async" width={320} height={240} data-id="element-313" data-mp="8u3mxx5dc40j" />
              </div>
            </motion.div>

            {/* Lifetime Access Badge */}
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-314" data-mp="muwm4v6m7n9z">
              <span className="bg-green-500/20 text-green-400 border border-green-500/30 font-black px-3 py-1 rounded-full text-[10px] uppercase tracking-widest shadow-lg" data-id="element-315" data-mp="11hputhlmput7">
                ACCESO VITALICIO
              </span>
            </motion.div>

            {/* Badge */}
            <span className="bg-yellow-500 text-blue-900 font-black px-3 py-1 rounded text-[10px] uppercase tracking-widest mb-4 shadow-lg" data-id="element-316" data-mp="1fi0mgmurjdff">
              BONO ESPECIAL
            </span>

            {/* Title */}
            <h2 className="text-xl md:text-3xl font-extrabold text-white mb-3 leading-tight uppercase tracking-tight" data-id="element-317" data-mp="xu87v9za0w2y">
              Certificado Profesional en <br className="hidden md:block" data-id="element-318" data-mp="1bkbljck3bhgs" />
              Comandos Eléctricos
            </h2>

            {/* Description */}
            <p className="text-xs md:text-sm text-blue-100 max-w-xl mb-6 leading-relaxed opacity-80" data-id="element-319" data-mp="iwftwm9dofqv">
              Al completar la guía, recibís un certificado digital de alta
              calidad listo para compartir con tus clientes y en redes sociales.
              Incluye una guía de una página sobre cómo usarlo para generar
              confianza y conseguir más trabajos.
            </p>

            {/* Included Gratis Badge */}
            <motion.div whileHover={{
            scale: 1.05
          }} className="inline-flex items-center gap-2 bg-blue-900/40 border border-blue-400/30 px-4 py-1.5 rounded-full" data-id="element-320" data-mp="248pidozg07qr">
              <Check className="w-3.5 h-3.5 text-blue-400 stroke-[3px]" data-id="element-321" data-mp="3h8169wam99dq" />
              <span className="text-blue-100 font-bold uppercase tracking-wider text-[10px]" data-id="element-322" data-mp="3m75g8p5d7b54">
                Incluido Gratis con tu compra
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section className="py-10 px-4 bg-[#0B1530] relative overflow-hidden" data-id="element-323" data-mp="3d6z3j5hhqf70">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        paddingTop: "4px"
      }} data-id="element-324" data-mp="4t9rpasvu6ra"></div>

        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-325" data-mp="3w40cf1e60zg8">
          <motion.h2 initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="md:text-4xl text-white mb-7 text-[28px] font-[700] font-[Poppins,_sans-serif] uppercase tracking-tight" data-id="element-326" data-mp="nphywk5ggrp8">
            LO QUE TE LLEVÁS CON MAESTRO ELÉCTRICO
          </motion.h2>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-327" data-mp="se0wi4dwhyqr">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/wZLFDJarxjC6YR14WGgqpR/Electrician_pointing_multimeter_202604231237.webp',
            title: 'CRITERIO Y SEGURIDAD',
            items: ['Más seguridad frente a los diagramas', 'Más criterio técnico'],
            tag: 'BASE TÉCNICA'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/vC9EYvMoTBEfW2EdR9JNKT/Industrial_control_panel_202604231237.webp',
            title: 'MENOS ERRORES',
            items: ['Menos errores', 'Menos dudas'],
            tag: 'MENOS RETRABAJO'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/gGvG1gp9LNad9JgT5vsQqK/Electrician_shaking_hands_202604231237.webp',
            title: 'PERFIL PROFESIONAL',
            items: ['Más confianza profesional', 'Mejor imagen ante el cliente'],
            tag: 'MEJOR IMAGEN'
          }].map((card, i) => <motion.div key={i} variants={itemVariants} whileHover={{
            y: -5
          }} className="bg-[#162447] border border-blue-500/20 rounded-2xl overflow-hidden shadow-2xl flex flex-col h-full" data-id="element-328" data-mp="2g9z3zebdapel" data-mpi={i}>
                {/* Image Area (40%) */}
                <div className="relative h-48 overflow-hidden" data-id="element-329" data-mp="1snir5yc8oxym">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={400} height={192} data-id="element-330" data-mp="1xlu2kdi6ngiy" />
                  {/* Gradient Overlay & Inner Shadow */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#162447] via-transparent to-black/20" data-id="element-331" data-mp="14xeq9yqfrcbb"></div>
                </div>

                {/* Content Area */}
                <div className="p-6 flex flex-col flex-1" data-id="element-332" data-mp="38nmhuxdot90">
                  <h3 className="font-bold text-white text-lg leading-tight tracking-tight mb-4 font-[Poppins,_sans-serif] uppercase" data-id="element-333" data-mp="397234zk35jv">
                    {card.title}
                  </h3>

                  <div className="space-y-3 mb-8 flex-1" data-id="element-334" data-mp="ca51tb2yoqwn">
                    {card.items.map((item, j) => <div key={j} className="flex items-start gap-3" data-id="element-335" data-mp="797kt1tou2wq" data-mpi={j}>
                        <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0 stroke-[3px]" data-id="element-336" data-mp="m6zv9fkdo142" />
                        <span className="text-gray-300 text-sm leading-snug" data-id="element-337" data-mp="h8yfb3i90hwt">
                          {item}
                        </span>
                      </div>)}
                  </div>

                  {/* Badge at bottom */}
                  <div className="mt-auto" data-id="element-338" data-mp="qyroxyytymyf">
                    <span className="inline-block bg-blue-600/20 text-blue-400 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.2)]" data-id="element-339" data-mp="14y0u83lhzwnm">
                      {card.tag}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="mt-8 bg-[#0f2044] border border-blue-800/40 text-white p-6 md:p-8 rounded-2xl text-center shadow-2xl" data-id="element-340" data-mp="1oj190votdcjm">
            <p className="text-lg md:text-xl font-semibold leading-relaxed font-[Poppins,_sans-serif] max-w-3xl mx-auto" data-id="element-341" data-mp="15l6y2c44kqyl">
              Invertís una vez en criterio. Eso se traduce en más trabajos,
              mejores decisiones y clientes que confían en vos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF CAROUSEL */}
      <section className="py-12 px-4 bg-gray-100 overflow-hidden" style={{
      paddingTop: "30px"
    }} data-id="element-342" data-mp="lwy1lpvjtyta">
        <div className="container mx-auto max-w-6xl" data-id="element-343" data-mp="350c3e2hv6qrz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-344" data-mp="351vivqvp4xsp">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-[Poppins,_sans-serif]" data-id="element-345" data-mp="3exp3lxjkjxlk">
              LO QUE DICEN OTROS PROFESIONALES
            </h2>
            <p className="text-gray-600" data-id="element-346" data-mp="3nzpzapz5mbl4">
              Más de 6.250 electricistas ya están aplicando este criterio
              técnico
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} className="relative" data-id="element-347" data-mp="1jlr55vovhhu">
            <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar" data-id="element-348" data-mp="6kj6lapbdxfn">
              {testimonialData.map((t, i) => <motion.div key={i} whileHover={{
              y: -5
            }} className="flex-shrink-0 w-[300px] md:w-[400px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden snap-center flex flex-col transition-all duration-300" data-id="element-349" data-mp="fiv7q6nt4dmc" data-mpi={i}>
                  <div className="aspect-square overflow-hidden relative" data-id="element-350" data-mp="1w1wwqtaco3ry">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={1080} height={1080} data-id="element-351" data-mp="1r1inx90xcpsv" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4" data-id="element-352" data-mp="2jp51na3v9atg">
                      <p className="text-white font-bold text-lg" data-id="element-353" data-mp="2ercz7m5li6b0">{t.name}</p>
                      <p className="text-blue-300 text-sm font-medium" data-id="element-354" data-mp="29z1wycndu8fa">
                        {t.study}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-355" data-mp="32fw7i1onaj33">
                    <div className="flex items-center gap-0.5 mb-4" data-id="element-356" data-mp="32h79bvyj5grk">
                      {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-357" data-mp="2xnl4gpylyh6b" />)}
                    </div>
                    <p className="text-gray-700 italic mb-6 flex-grow" data-id="element-358" data-mp="vtqf7prcjonv">
                      "{t.comment}"
                    </p>
                    <div className="pt-4 border-t border-gray-100" data-id="element-359" data-mp="m2cb14oga0oe">
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1" data-id="element-360" data-mp="lzzvsza2u4f1">
                        Estudió en:
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-361" data-mp="c2v7assrdwv2">
                        {t.study}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Visual indicators for mobile */}
            <div className="flex justify-center gap-2 mt-2 md:hidden" data-id="element-362" data-mp="81jgijy5f5la">
              {testimonialData.map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" data-id="element-363" data-mp="1xgpttv4cjpxk" data-mpi={i}></div>)}
            </div>

            <p className="text-center text-sm text-gray-400 mt-4 flex items-center justify-center gap-2" data-id="element-364" data-mp="22gc90a3qjg90">
              <ChevronLeft className="w-4 h-4" data-id="element-365" data-mp="2cl2cwlip8ufv" /> Deslizá para ver más{' '}
              <ChevronRight className="w-4 h-4" data-id="element-366" data-mp="27lg18giirm96" />
            </p>
          </motion.div>
        </div>
      </section>

      {/* PURCHASE SECTION - TWO CARDS */}
      <section id="compra" className="py-7 bg-[#EFF6FF] relative overflow-hidden" data-id="element-367" data-mp="2leffkf6kfaf5">
        <div className="container mx-auto px-2 md:px-4 relative z-10" data-id="element-368" data-mp="2gk1hu64fkr7b">
          <div className="max-w-4xl mx-auto" data-id="element-369" data-mp="2v9peuv5kx3p8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-5" data-id="element-370" data-mp="2vclfir8ta6hd">
              <h2 className="text-2xl md:text-4xl font-black text-gray-900 uppercase tracking-tight" data-id="element-371" data-mp="30925lqzkmux3">
                ⚡ LLEVÁ TU CONOCIMIENTO AL SIGUIENTE NIVEL
              </h2>
            </motion.div>

            {/* New Reference-style Offer Container */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.98
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 100,
            damping: 20
          }} className="w-full max-w-md mx-auto rounded-3xl bg-[#EFF6FF] border border-blue-100 overflow-hidden mb-5 shadow-[0_14px_32px_rgba(30,64,175,0.12)] font-[Poppins,_sans-serif]" data-id="element-372" data-mp="15xnfh92netgt">
              <div className="px-3 py-3 md:p-4" data-id="element-373" data-mp="ok5fixoo9bw8">
                <article className="rounded-2xl bg-white p-5 shadow-[0_12px_28px_rgba(1,13,35,0.2)]" data-id="element-374" data-mp="sn1xqnaav5zn">
                  <h3 className="text-center text-lg font-black tracking-tight text-[#101F40]" data-id="element-375" data-mp="xlvf4qspjh53">¿QUÉ ESTÁS LLEVANDO?</h3>
                  <img src="https://cdn.magicpatterns.com/uploads/tnVQvKnN3svZA8vg51tgKr/Pack_Maestro_Electrico_Industrial.webp" alt="Pack Maestro Eléctrico Industrial con manual y bonos incluidos" className="mx-auto mt-3 block w-full max-w-[340px] h-auto object-contain" loading="lazy" decoding="async" data-id="element-376" data-mp="12f7pbvwn3leb" />
                  <ul className="mt-4 space-y-3 text-sm font-semibold text-slate-700" data-id="element-377" data-mp="2d1m0cg84h5p">
                    <li className="flex items-start gap-2" data-id="element-378" data-mp="3tknjfnccnne4"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#16A34A]" strokeWidth={3} data-id="element-379" data-mp="3tib0p7yrp900" />Manual Maestro Eléctrico</li>
                    <li className="flex items-start gap-2" data-id="element-380" data-mp="3kk7iakj2wscf"><Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#F5C542]" fill="currentColor" data-id="element-381" data-mp="3kiwievsr84ql" />Bono 1: Eficiencia en Motores</li>
                    <li className="flex items-start gap-2" data-id="element-382" data-mp="3atmnz9afu9zb"><Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#F5C542]" fill="currentColor" data-id="element-383" data-mp="3asl8msme31bs" />Bono 2: Plantillas de Presupuesto</li>
                    <li className="flex items-start gap-2" data-id="element-384" data-mp="35sgpeuu3jshv"><Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#F5C542]" fill="currentColor" data-id="element-385" data-mp="30vypc89poibv" />Bono 3: Protocolo de Energía</li>
                    <li className="flex items-start gap-2" data-id="element-386" data-mp="2wtbwc41b0rd3"><Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#F5C542]" fill="currentColor" data-id="element-387" data-mp="2rus6vtni4xfz" />Bono 4: Curso de Paneles Solares</li>
                    <li className="flex items-start gap-2" data-id="element-388" data-mp="2i63cketxjdpi"><Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#F5C542]" fill="currentColor" data-id="element-389" data-mp="2i993lzm4cpnv" />Bono 5: Reglamentaciones AEA</li>
                  </ul>
                  <div className="mt-4 rounded-xl bg-[#1D4ED8] px-4 py-3 text-center text-sm font-black text-white" data-id="element-390" data-mp="28a0ebk277z7u">🏅 EXTRA: Certificado Profesional</div>
                </article>
                <div className="py-5 text-center" data-id="element-391" data-mp="30x4bx3yypb9l">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500" data-id="element-392" data-mp="30yfo9s6h3rcb">Inversión única</p>
                  <p className="mt-1 text-6xl font-black tracking-tighter leading-none text-[#00A85A]" data-id="element-393" data-mp="35yjverlhr9pv">$19.990 <span className="text-sm font-extrabold tracking-normal text-slate-500" data-id="element-394" data-mp="2r5qdz0mnsjik">ARS</span></p>
                </div>
                <button onClick={handlePurchaseClick} className="w-full rounded-2xl bg-[linear-gradient(105deg,#22C55E,#16A34A)] px-4 py-4 text-base font-black leading-tight text-white shadow-[0_7px_0_#0F6E31,0_14px_22px_rgba(22,163,74,0.3)] transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 active:translate-y-1 active:shadow-[0_3px_0_#0F6E31]" data-id="element-395" data-mp="3jwrzdjgy3baj">
                  <span className="block" data-id="element-396" data-mp="1872coi0swj59">⚡ ¡SÍ! QUIERO</span><span className="block" data-id="element-397" data-mp="1m130igq7slqu">MAESTRO ELÉCTRICO</span>
                </button>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[10px] font-black tracking-wide text-[#15803D]" data-id="element-398" data-mp="1hz7ytb4dg6cj">
                  <span className="flex items-center gap-1 rounded-full border border-green-100 bg-white px-3 py-1.5 shadow-sm" data-id="element-399" data-mp="u9x9cpsh1ydp"><Zap className="h-3.5 w-3.5" data-id="element-400" data-mp="pc564zmdmpu7" /> ACCESO INMEDIATO</span>
                  <span className="flex items-center gap-1 rounded-full border border-green-100 bg-green-50 px-3 py-1.5 shadow-sm" data-id="element-401" data-mp="13b4t82u8qwo9"><Lock className="h-3.5 w-3.5" data-id="element-402" data-mp="ya7g44o38stm" /> PAGO SEGURO</span>
                </div>
                {false && <div className="hidden space-y-2" role="radiogroup" aria-label="Selector de packs anterior" data-id="element-403" data-mp="2enxmxj6ldv6y">
                  <div className={`overflow-hidden rounded-xl border-2 transition-[border-color,background-color] duration-200 ${selectedOffer === 'solo' ? 'border-[#2FBF71] bg-[#153A2C]' : 'border-[#285A43] bg-[#153A2C]'}`} data-id="element-404" data-mp="29pmdh9k7lmp9">
                    <motion.button whileTap={{
                    scale: 0.99
                  }} transition={{
                    duration: 0.2
                  }} onClick={() => setSelectedOffer('solo')} role="radio" aria-checked={selectedOffer === 'solo'} className="w-full min-h-[64px] relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 bg-transparent p-3 text-left" data-id="element-405" data-mp="2yoz2cidbnv77">
                    <div className="min-w-0 pl-8" data-id="element-406" data-mp="3hhbf7z1lqevl">
                      <p className="mb-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-[#4ADE80]" data-id="element-407" data-mp="3hg03nd1x02tx">Esencial</p>
                      <h4 className="text-[14px] font-bold text-white leading-tight uppercase" data-id="element-408" data-mp="3mf340k6y9kvk">Solo Maestro Eléctrico</h4>
                      <p className="mt-1 text-[12px] font-medium text-green-100/75 leading-snug" data-id="element-409" data-mp="1s3eoafsw9ea4">Sistema avanzado + 6 bonos</p>
                    </div>
                    <div className="flex items-center justify-end" data-id="element-410" data-mp="1w4ruws3t9li3">
                      <span className="text-[17px] font-extrabold text-[#4ADE80] whitespace-nowrap" data-id="element-411" data-mp="1om0pkho7l5bi">$19.990</span>
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'solo' ? 'border-[#4ADE80]' : 'border-green-300/60'}`} data-id="element-412" data-mp="1jsphkd7nuii0">
                        {selectedOffer === 'solo' && <motion.span layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-[#4ADE80]" data-id="element-413" data-mp="27jl5gz8zwtjz" />}
                      </span>
                    </div>
                    </motion.button>
                    <AnimatePresence initial={false} data-id="element-414" data-mp="1yiws86rfex85">
                      {selectedOffer === 'solo' && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="overflow-hidden px-3 pt-2.5 pb-2.5" style={{
                      paddingTop: "0px",
                      paddingBottom: "4px"
                    }} data-id="element-415" data-mp="1tk29t5xdfaok">
                        <ul className="space-y-1 text-[11px] font-semibold text-green-50" data-id="element-416" data-mp="3fvm4k0tlpgau">
                          {offerModalDetails.solo.benefits.map(benefit => <li key={benefit} className="flex gap-2" data-id="element-417" data-mp="3jxh7tav3e3r9"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#4ADE80]" data-id="element-418" data-mp="3ow0vpgt8xpm9" />{benefit}</li>)}
                        </ul>
                          <button onClick={() => setIsOfferModalOpen(true)} className="pack-detail-button pack-detail-button-green mt-2 w-full rounded-lg border border-green-300/30 bg-white/10 py-2 text-[11px] font-black tracking-wide text-green-100" data-id="element-419" data-mp="3tnsn1tk87zdg"><span data-id="element-420" data-mp="7dzloivow6rl">🎁 VER TODO LO QUE INCLUYE</span><span className="pack-detail-arrow" data-id="element-421" data-mp="bh4ic4t39e8b">→</span></button>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  <div className="relative pt-2.5" data-id="element-422" data-mp="ge4pkj1ocn10">
                    <span className="absolute top-0 left-3 z-10 whitespace-nowrap rounded-full bg-[#60A5FA] px-2 py-0.5 text-[8px] font-black text-[#101F40] tracking-wide" data-id="element-423" data-mp="2d9anrd3f9fe2">⭐ MÁS ELEGIDO</span>
                    <div className={`overflow-hidden rounded-xl border-2 transition-[border-color,background-color] duration-200 ${selectedOffer === 'v2' ? 'border-[#4E8DE8] bg-[#172C53]' : 'border-[#294B7E] bg-[#172C53]'}`} data-id="element-424" data-mp="2aoclvnxqnf42">
                      <motion.button whileTap={{
                      scale: 0.99
                    }} transition={{
                      duration: 0.2
                    }} onClick={() => setSelectedOffer('v2')} role="radio" aria-checked={selectedOffer === 'v2'} className="w-full min-h-[64px] relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 bg-transparent p-3 text-left" data-id="element-425" data-mp="20z1cs1auqag6">
                    <div className="min-w-0 pl-8" data-id="element-426" data-mp="2olrfz5yxsb2d">
                      <p className="mb-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-[#60A5FA]" data-id="element-427" data-mp="2jnzna9qgwkx4">Profesional</p>
                      <h4 className="text-[14px] font-bold text-white leading-tight uppercase" data-id="element-428" data-mp="2fme6weeq5oi6">Maestro Eléctrico 2.0</h4>
                      <p className="mt-1 text-[12px] font-medium text-blue-100 leading-snug" data-id="element-429" data-mp="187hqg8iykg9y">Nueva versión + 10 bonos</p>
                    </div>
                    <div className="flex items-center justify-end" data-id="element-430" data-mp="15omnxu0bhlhc">
                      <span className="text-[17px] font-extrabold text-[#93C5FD] whitespace-nowrap" data-id="element-431" data-mp="15nbkjvasafqr">$26.990</span>
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'v2' ? 'border-[#60A5FA]' : 'border-blue-300/60'}`} data-id="element-432" data-mp="vr8lwzf54z8k">
                        {selectedOffer === 'v2' && <motion.span layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-[#93C5FD]" data-id="element-433" data-mp="mxwq4jplsovc" />}
                      </span>
                    </div>
                  </motion.button>
                    <AnimatePresence initial={false} data-id="element-434" data-mp="d220v5qvjvjl">
                      {selectedOffer === 'v2' && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.3,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="overflow-hidden px-3 pt-2.5 pb-2.5" style={{
                        paddingTop: "0px",
                        paddingBottom: "4px"
                      }} data-id="element-435" data-mp="i0n0b2yayb6z">
                        <ul className="space-y-1 text-[11px] font-semibold text-blue-50" data-id="element-436" data-mp="3lj1c53km169g">
                          {offerModalDetails.v2.benefits.map(benefit => <li key={benefit} className="flex gap-2" data-id="element-437" data-mp="3uj68mio9mk8u"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#93C5FD]" data-id="element-438" data-mp="3uk8xym5xwemn" />{benefit}</li>)}
                        </ul>
                        <button onClick={() => setIsOfferModalOpen(true)} className="pack-detail-button pack-detail-button-blue mt-2 w-full rounded-lg border border-blue-300/40 bg-white/10 py-2 text-[11px] font-black tracking-wide text-[#DBEAFE]" data-id="element-439" data-mp="3pm7fo3gc8jc8"><span data-id="element-440" data-mp="h49w18vxmcvy">🎁 VER TODO LO QUE INCLUYE</span><span className="pack-detail-arrow" data-id="element-441" data-mp="h38b8cp46c11">→</span></button>
                      </motion.div>}
                    </AnimatePresence>
                    </div>
                  </div>

                  <div className="relative pt-2.5" data-id="element-442" data-mp="r3imhrrl99dk">
                    <span className="absolute top-0 left-3 z-10 whitespace-nowrap rounded-full border border-[#F3D889] bg-[#D6B15D] px-2 py-0.5 text-[8px] font-black text-[#17120A] tracking-wide" data-id="element-443" data-mp="r04oevpyjmbx">🔥 MEJOR VALOR</span>
                    <div className={`overflow-hidden rounded-xl border-2 transition-[border-color,background-color] duration-200 ${selectedOffer === 'combo' ? 'border-[#F5C542] bg-[#302711] shadow-[0_0_16px_rgba(245,197,66,0.18)]' : 'border-[#8E6B1C] bg-[#302711]'}`} data-id="element-444" data-mp="1a6uai2roaanb">
                    <motion.button whileTap={{
                      scale: 0.99
                    }} transition={{
                      duration: 0.2
                    }} onClick={() => setSelectedOffer('combo')} role="radio" aria-checked={selectedOffer === 'combo'} className="w-full min-h-[64px] relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 bg-transparent p-3 text-left" style={{
                      paddingTop: "12px",
                      paddingBottom: "10px"
                    }} data-id="element-445" data-mp="1jyzxey32i1vn">
                    <div className="min-w-0 pl-8" data-id="element-446" data-mp="1141hnv2udqvd">
                      <p className="mb-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-[#F5C542]" data-id="element-447" data-mp="27lewcg5g8ng2">Premium</p>
                      <h4 className="text-[14px] font-bold text-white leading-tight uppercase" data-id="element-448" data-mp="2cjzvsdcvn33g">Combo Industrial Completo</h4>
                      <p className="mt-1 text-[12px] font-medium text-slate-300 leading-snug" data-id="element-449" data-mp="1023sl8ozi9h">Sistema completo + 15 bonos</p>
                    </div>
                    <div className="flex items-center justify-end" data-id="element-450" data-mp="3ncqvoz6qyx9r">
                      <span className="text-[17px] font-extrabold text-[#E7C56A] whitespace-nowrap" data-id="element-451" data-mp="3iez1tzmjl12x">$34.990</span>
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'combo' ? 'border-[#E7C56A]' : 'border-[#80692C]'}`} data-id="element-452" data-mp="3ednczwbhsjvs">
                        {selectedOffer === 'combo' && <motion.span layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-[#E7C56A]" data-id="element-453" data-mp="34f6duc2egyyn" />}
                      </span>
                    </div>
                    </motion.button>
                    <AnimatePresence initial={false} data-id="element-454" data-mp="12lgwtqhcvaxg">
                      {selectedOffer === 'combo' && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.3,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="overflow-hidden px-3 pt-2.5 pb-2.5" style={{
                        paddingTop: "0px"
                      }} data-id="element-455" data-mp="xp01ocbqnwav">
                        <ul className="space-y-1 text-[11px] font-semibold text-white" data-id="element-456" data-mp="2n6dmdfe09afs">
                          {offerModalDetails.combo.benefits.map(benefit => <li key={benefit} className="flex gap-2" data-id="element-457" data-mp="3atde037hla5k"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#F5C542]" data-id="element-458" data-mp="3fuj7w4cvv3go" />{benefit}</li>)}
                        </ul>
                        <button onClick={() => setIsOfferModalOpen(true)} className="pack-detail-button pack-detail-button-gold mt-2 w-full rounded-lg border border-[#F5C542]/70 bg-white/10 py-2 text-[11px] font-black tracking-wide text-[#FBE7A0]" data-id="element-459" data-mp="31u8hf2fhjqw1"><span data-id="element-460" data-mp="1qhpidmz4jnpd">🎁 VER TODO LO QUE INCLUYE</span><span className="pack-detail-arrow" data-id="element-461" data-mp="1uh6omimdkovl">→</span></button>
                      </motion.div>}
                    </AnimatePresence>
                    </div>
                  </div>
                </div>}

                <div className="hidden" aria-hidden="true" data-id="element-462" data-mp="1hxj95cvtvqb5">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-200/70" data-id="element-463" data-mp="1ww4un3sif06f">Inversión única</p>
                  <p className="mt-0.5 text-xs font-bold text-blue-100/45 line-through" data-id="element-464" data-mp="1rxjtn25focgx">Antes {activeOffer.originalPrice} ARS</p>
                  <div className={`mt-0.5 inline-flex items-baseline justify-center gap-2 ${selectedOffer === 'combo' ? 'relative isolate' : ''}`} data-id="element-465" data-mp="1477cr1s7n5e7">
                    {selectedOffer === 'combo' && <span aria-hidden="true" className="absolute -inset-x-5 -inset-y-3 -z-10 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(7,26,53,0.24),rgba(7,26,53,0.09)_46%,transparent_72%)] blur-md" data-id="element-466" data-mp="1dxit5nt2z71z" />}
                    <motion.span key={selectedOffer} initial={{
                    opacity: 0,
                    y: 6,
                    scale: 0.85
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1]
                  }} className={`pack-price-entry text-5xl md:text-6xl font-black tracking-tighter leading-none ${selectedOffer === 'combo' ? 'pack-price-glow pack-price-gold text-[#E7C56A]' : selectedOffer === 'v2' ? 'pack-price-glow pack-price-blue text-[#2563EB]' : 'pack-price-glow pack-price-green text-[#16A34A]'}`} data-id="element-467" data-mp="lev98bptly2h">
                      {activeOffer.price}
                    </motion.span>
                    <span className={`text-sm font-black ${selectedOffer === 'combo' ? 'text-[#9A6700]' : 'text-slate-400'}`} data-id="element-468" data-mp="ivft0l9cujo9">ARS</span>
                  </div>
                </div>

                <div className="hidden" aria-hidden="true" data-id="element-469" data-mp="slsmj2z5dn6j">
                  <motion.button key={`cta-${selectedOffer}`} initial={selectedOffer === 'combo' ? {
                  opacity: 0.9,
                  y: 4
                } : false} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.25
                }} whileHover={{
                  y: -2
                }} whileTap={{
                  y: 2,
                  scale: 0.99
                }} onClick={selectedOffer === 'combo' ? handleComboPurchaseClick : selectedOffer === 'v2' ? handleV2PurchaseClick : handlePurchaseClick} className={`pack-main-cta flex w-full min-h-[68px] items-center justify-center rounded-xl px-4 text-white font-black text-lg md:text-xl uppercase tracking-tight relative overflow-hidden ${selectedOffer === 'combo' ? 'pack-main-cta-gold bg-[linear-gradient(105deg,#C8941C,#9A6800,#C8941C)]' : selectedOffer === 'v2' ? 'pack-main-cta-blue bg-[#2563EB]' : 'pack-main-cta-green bg-[#16A34A]'}`} data-id="element-470" data-mp="sl92ngpxsopv">
                    <span className="relative z-10 inline-flex items-center justify-center gap-1.5" data-id="element-471" data-mp="2w2fj4til799m">{activeOffer.cta.slice(0, -1)}<span className="pack-main-cta-arrow" data-id="element-472" data-mp="2r449aisakyre">→</span></span>
                  </motion.button>
                  <div className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 ${selectedOffer === 'combo' ? 'text-[#FBE7A0]' : selectedOffer === 'v2' ? 'text-[#BFDBFE]' : 'text-[#BBF7D0]'}`} data-id="element-473" data-mp="3oj8bw9jzftwl">
                    <span className="flex items-center gap-1 text-[10px] font-black tracking-wide" data-id="element-474" data-mp="3bn3bzelucu89"><Zap className="w-3.5 h-3.5" data-id="element-475" data-mp="37kykaleye7o9" /> ACCESO INMEDIATO</span>
                    <span className={`hidden sm:block w-1 h-1 rounded-full ${selectedOffer === 'combo' ? 'bg-[#F5C542]' : selectedOffer === 'v2' ? 'bg-[#60A5FA]' : 'bg-[#4ADE80]'}`} data-id="element-476" data-mp="3lk819nazh5ta" />
                    <span className="flex items-center gap-1 text-[10px] font-black tracking-wide" data-id="element-477" data-mp="3gn7vzelyk738"><Shield className="w-3.5 h-3.5" data-id="element-478" data-mp="3ujd0kvudd36i" /> GARANTÍA DE 30 DÍAS</span>
                  </div>
                </div>

                <AnimatePresence mode="wait" data-id="element-479" data-mp="3qhhyvq8j0ns7">
                  <motion.div key={selectedOffer} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="hidden mt-2" data-id="element-480" data-mp="84a2bcw6opjd">
                    <div className="overflow-hidden rounded-xl border border-[#294B7E] bg-[#0C1A36]" data-id="element-481" data-mp="i40xzhwhdal1">
                      <img src={activeOffer.preview} alt={activeOffer.previewAlt} className="block w-full h-auto max-h-[280px] object-contain" loading="lazy" decoding="async" data-id="element-482" data-mp="d5zh93mj1ncq" />
                    </div>
                    {selectedOffer === 'combo' && <div className="mt-2 flex items-center gap-2 rounded-xl bg-[#FFF8E6] px-3 py-2 text-left border border-[#E7C56A]" data-id="element-483" data-mp="10pgvdt30v9fl">
                        <Award className="w-4 h-4 text-[#9A6700] flex-shrink-0" data-id="element-484" data-mp="rqbx8nvdhi3y" />
                        <div data-id="element-485" data-mp="d2h10xi5ovm4">
                          <p className="text-xs font-black text-[#624A08]" data-id="element-486" data-mp="d1ffu07fesqo">¡DESBLOQUEASTE EL PACK MÁS COMPLETO!</p>
                          <p className="text-[10px] font-medium text-[#7A6011]" data-id="element-487" data-mp="huibcdvrkc9i">Todos los manuales del combo + 15 bonos incluidos.</p>
                        </div>
                      </div>}
                  </motion.div>
                </AnimatePresence>

                <AnimatePresence data-id="element-488" data-mp="3q88tnlesgoar">
                  {isOfferModalOpen && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4" role="presentation" onClick={() => setIsOfferModalOpen(false)} data-id="element-489" data-mp="3q9is4bpjzyhx">
                      <motion.section initial={{
                    opacity: 0,
                    y: 12,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 12,
                    scale: 0.98
                  }} transition={{
                    duration: shouldReduceMotion ? 0 : 0.2
                  }} role="dialog" aria-modal="true" aria-labelledby="offer-modal-title" className="flex max-h-[82vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-490" data-mp="37axkmwp491xj">
                        <header className={`flex items-center justify-between px-5 py-4 text-white ${selectedOffer === 'combo' ? 'bg-[#071A35]' : 'bg-[#172554]'}`} data-id="element-491" data-mp="3g4tzrrjv5e3g">
                          <div data-id="element-492" data-mp="29q1k7nq66jct">
                            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-white/70" data-id="element-493" data-mp="24pnbs4knp7e9">Detalle del pack</p>
                            <h3 id="offer-modal-title" className="text-lg font-black" data-id="element-494" data-mp="k8e4ujrwy9vv">{activeModalDetails.title}</h3>
                          </div>
                          <button onClick={() => setIsOfferModalOpen(false)} aria-label="Cerrar detalle del pack" className="rounded-full p-2 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-495" data-mp="p4mpudsskxay"><X className="h-5 w-5" data-id="element-496" data-mp="13w4zzuyyzxme" /></button>
                        </header>
                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" data-id="element-497" data-mp="17wx9mpbr84j7">
                          <img src={activeOffer.preview} alt={activeOffer.previewAlt} className="mx-auto block w-full h-auto max-h-52 object-contain" loading="lazy" data-id="element-498" data-mp="17z1kxsbzg5ti" />
                          <div className={`mt-3 rounded-xl px-3 py-2.5 ${selectedOffer === 'combo' ? 'border border-[#F5C542]/60 bg-[#FFF8E6]' : 'border border-green-200 bg-green-50'}`} data-id="element-499" data-mp="1huuzfh99i9dx">
                            <p className={`text-sm font-black leading-snug ${selectedOffer === 'combo' ? 'text-[#7A5200]' : 'text-[#166534]'}`} data-id="element-500" data-mp="3jnj5rzh7dcxx">{activeModalDetails.summary}</p>
                            {selectedOffer === 'combo' && <p className="mt-1 text-[11px] font-bold text-[#9A6700]" data-id="element-501" data-mp="18yupfyg4c6y4">El pack más completo.</p>}
                          </div>
                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-id="element-502" data-mp="1ixk8vvnnxzgh">
                            <p className="text-[10px] font-black uppercase tracking-wide text-slate-500" data-id="element-503" data-mp="v7px7hf2ygxt">Precio especial · {currentDate}</p>
                            <p className="mt-1 text-[11px] font-semibold leading-snug text-slate-700" data-id="element-504" data-mp="qa66hi174boz">⚡ Aprovechá el precio vigente. Puede cambiar en futuras visitas.</p>
                          </div>
                          <h4 className="mt-4 text-sm font-black text-slate-900" data-id="element-505" data-mp="z8rqlhmbfyrv">Incluye</h4>
                          <ul className="mt-2 space-y-2" data-id="element-506" data-mp="binvxzr3v9d0">
                            {activeModalDetails.included.map(item => <li key={item} className="flex gap-3 text-sm leading-snug text-slate-700" data-id="element-507" data-mp="7hltzb5t0j7c"><Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${selectedOffer === 'combo' ? 'text-[#B77900]' : 'text-green-600'}`} data-id="element-508" data-mp="3tjb1e466049l" />{item}</li>)}
                          </ul>
                        </div>
                        <footer className="border-t border-slate-200 bg-white px-5 py-3.5 shadow-[0_-8px_20px_rgba(15,23,42,0.06)]" data-id="element-509" data-mp="3jmpvlgmbmp4p">
                          <div className="flex items-end justify-between gap-3" data-id="element-510" data-mp="3oindhrd8g3jl">
                            <div data-id="element-511" data-mp="g5hfb178rxc3">
                              <p className="text-[10px] font-bold text-slate-400 line-through" data-id="element-512" data-mp="l413l89b5l7m">Antes {activeOffer.originalPrice} ARS</p>
                              <p className="mt-0.5 text-xs font-bold text-slate-500" data-id="element-513" data-mp="67jwit85isqv">Precio final</p>
                            </div>
                            <span className={`text-3xl font-black ${selectedOffer === 'combo' ? 'text-[#C49016]' : 'text-[#16A34A]'}`} data-id="element-514" data-mp="b40hxfnyut09">{activeOffer.price} <span className="text-sm" data-id="element-515" data-mp="2vw2m4qwrbrg1">ARS</span></span>
                          </div>
                          <button onClick={selectedOffer === 'combo' ? handleComboPurchaseClick : selectedOffer === 'v2' ? handleV2PurchaseClick : handlePurchaseClick} className={`mt-3 w-full rounded-xl py-3 text-sm font-black text-white ${selectedOffer === 'combo' ? 'bg-[#B77900] shadow-[0_4px_0_#7A5000]' : 'bg-[#16A34A] shadow-[0_4px_0_#14532D]'}`} data-id="element-516" data-mp="12ddfbsatsa7o">COMPRAR AHORA →</button>
                        </footer>
                      </motion.section>
                    </motion.div>}
                </AnimatePresence>

                {/* Technical Standards */}
                <div className="mt-4 rounded-2xl border border-slate-100 bg-white px-3 py-2 shadow-sm" data-id="element-517" data-mp="em7oyj79dzj4">
                  <img src="https://cdn.magicpatterns.com/uploads/45AKbXKjJnZm5Z1fHy751C/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="mx-auto w-full max-w-[260px] h-auto object-contain" loading="lazy" decoding="async" width={260} height={65} data-id="element-518" data-mp="9p7sn007ob57" />
                </div>

                {/* Social Proof Image */}
                

                {/* Social Proof Widget */}
                <div className="mt-6 rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-white relative" data-id="element-519" data-mp="26mly1i7b3bgd">
                  {/* Date Banner */}
                  <div className="px-4 py-3 text-center border-b border-yellow-500/20 relative z-10" style={{
                  paddingTop: "4px",
                  paddingBottom: "0px"
                }} data-id="element-520" data-mp="2gd83ijlb6llc">
                    <p className="text-gray-900 text-xs md:text-sm font-black tracking-wide uppercase" data-id="element-521" data-mp="2ldvwhat3lci7">
                      OFERTA VÁLIDA SOLO POR HOY:{' '}
                      <span className="text-yellow-600" data-id="element-522" data-mp="1so5eurs9bshc">
                        {weekdayDate.toUpperCase()}
                      </span>
                    </p>
                  </div>

                  {/* Viewers & Cupos Bar */}
                  <div className="grid grid-cols-2 divide-x divide-gray-200 relative z-10" data-id="element-523" data-mp="22jgxhl6sdjud">
                    {/* Viewers */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-524" data-mp="3e0go8cdhjqma">
                      <div className="flex items-center gap-1.5" data-id="element-525" data-mp="2zd4pfb57idy">
                        <span className="relative flex h-3 w-3" data-id="element-526" data-mp="2zmw97bu5xlr">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-527" data-mp="3u6fymeid82rv"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" data-id="element-528" data-mp="cqhkuick19ad"></span>
                        </span>
                        <span className="text-green-600 text-2xl md:text-3xl font-black" data-id="element-529" data-mp="lpl1zgs5vem8">
                          {viewers}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-530" data-mp="hqlym0m2vpww">
                        <p className="text-gray-600 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-531" data-mp="vmzfp8mp9pau">
                          PERSONAS
                          <br data-id="element-532" data-mp="qn4u9klp1rx6" />
                          MIRANDO
                          <br data-id="element-533" data-mp="2xfxfxpit3k5t" />
                          EN ESTE MOMENTO
                        </p>
                      </div>
                    </div>

                    {/* Cupos */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-534" data-mp="17zl4qmhwqkgi">
                      <div className="flex items-center gap-1.5" data-id="element-535" data-mp="1cwbih368p3yr">
                        <span className="text-red-600 text-2xl md:text-3xl font-black" data-id="element-536" data-mp="y8qhbesxdeth">
                          {urgencySpots}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-537" data-mp="13577sfnlk59q">
                        <p className="text-gray-600 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-538" data-mp="p6zcsda4fe17">
                          CUPOS
                          <br data-id="element-539" data-mp="u4irtfen7uu7" />
                          PROMOCIONALES
                          <br data-id="element-540" data-mp="fb5ued9q0qbi" />
                          DISPONIBLES
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Buyer Counter */}
                  <div className="px-4 py-4 text-center border-t border-gray-200 relative z-10" style={{
                  paddingBottom: "0px",
                  paddingTop: "0px"
                }} data-id="element-541" data-mp="29lj5kfmibl4s">
                    <motion.p key={mainBuyers} initial={{
                    scale: 1.1,
                    opacity: 0.7
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.3
                  }} className="text-green-600 text-4xl md:text-5xl font-black tracking-tight" data-id="element-542" data-mp="3i5tuip14ztyd">
                      {mainBuyers.toLocaleString('es-AR')}
                    </motion.p>
                    <img src="https://cdn.magicpatterns.com/uploads/bMEmGhKJnbWQozQy9C37z7/ChatGPT_Image_8_ago_2026,_23_17_17.png" alt="Más de 3000 alumnos con el curso completo" className="w-full h-auto object-cover" loading="lazy" data-id="element-543" data-mp="5oygpywg8ghz" /><p className="text-gray-600 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-544" data-mp="3rvmvyx7kesow">
                      PROFESIONALES YA ACCEDIERON
                    </p>
                  </div>
                </div>

                {/* Trust Badges Section */}
                <div className="mt-8 flex flex-col items-center gap-6 animate-fade-in-up-soft" data-id="element-545" data-mp="2lgck5wr7ljqc">
                  {/* Placeholder for the new images with adjusted heights */}
                  
                  <div className="flex items-center justify-center md:gap-10 gap-[10px]" data-id="element-546" data-mp="28ya690le94d9">
                    <img src="https://cdn.magicpatterns.com/uploads/6sMJ49nAT1B4ubV5kxhyZE/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 w-auto object-contain" loading="lazy" decoding="async" width={100} height={80} data-id="element-547" data-mp="2mxt0qqla8h73" />
                    <img src="https://cdn.magicpatterns.com/uploads/xuS8xz2qt356pzvFYDWisp/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-32 md:h-40 w-auto object-contain" loading="lazy" decoding="async" width={120} height={128} data-id="element-548" data-mp="1q8270tktey2c" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* MercadoLíder Platinum Trust Seal */}
            <img src="https://cdn.magicpatterns.com/uploads/gJq4qKV8FmpwVqN9zDNoQa/capture_261005_175233.png" alt="Referencias técnicas argentinas: AEA 90364 e IRAM" className="w-full max-w-[360px] h-auto object-contain" loading="lazy" decoding="async" data-id="element-549" data-mp="16ii2vwfoae0n" /><div className="flex justify-center" data-id="element-550" data-mp="3bisx48brslwn">
              <img src="https://cdn.magicpatterns.com/uploads/wihdMwuqBpQKvri8XHgUZZ/Gemini_Generated_Image_jbp7w2jbp7w2jbp7.png" alt="MercadoLíder Platinum - 8413 ventas" className="w-full max-w-2xl object-contain" loading="lazy" decoding="async" width={672} height={100} data-id="element-551" data-mp="3gcnaku0s84na" />
            </div>

            {/* Lifetime Access Bonus Section */}
            <div className="mt-8 mb-6 flex justify-center px-4" data-id="element-552" data-mp="2so43uixdrzxb">
              <div className="w-full max-w-2xl bg-white rounded-[12px] border border-gray-200 shadow-2xl overflow-hidden relative group" data-id="element-553" data-mp="2xkba6ctovdfs">
                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 bg-gray-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-554" data-mp="cvn766htsi5v"></div>

                <div className="p-6 md:p-8 flex flex-col items-center text-center relative z-10 pl-[15px] pr-[15px]" style={{
                paddingTop: "13px",
                paddingBottom: "6px"
              }} data-id="element-555" data-mp="gunotlokhera">
                  {/* Authority Seal */}
                  <div className="relative mb-8" data-id="element-556" data-mp="1xf0qyyqg3p0o">
                    <div className="absolute inset-0 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" data-id="element-557" data-mp="1sleh1ebo1z8k"></div>
                    <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full shadow-[0_0_50px_rgba(234,179,8,0.2)] border-4 border-yellow-500/10 overflow-hidden" data-id="element-558" data-mp="1nmbj0du3sta3">
                      <img src="https://cdn.magicpatterns.com/uploads/oLERSYzpMkuFusC7XS9pGH/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Sello de Acceso Vitalicio" className="w-full h-full object-cover" loading="lazy" decoding="async" width={192} height={192} data-id="element-559" data-mp="1dof91pcwk4dk" />
                    </div>
                  </div>

                  {/* Text Hierarchy */}
                  <span className="text-green-600 text-[10px] md:text-xs font-black tracking-[0.2em] uppercase mb-3" data-id="element-560" data-mp="33za3z98tbcac">
                    BONO EXTRA EXCLUSIVO
                  </span>

                  <h3 className="text-gray-900 text-3xl md:text-4xl font-bold mb-4 tracking-tight" data-id="element-561" data-mp="1vrvwdslnbpqf">
                    Acceso Vitalicio
                  </h3>

                  <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-lg mb-8" data-id="element-562" data-mp="1mrh58cm03gf0">
                    Olvidate de las mensualidades. Con un solo pago tenés acceso
                    de por vida a todo el material y a las futuras
                    actualizaciones sin pagar un centavo más.
                  </p>

                  {/* Status Component */}
                  <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-green-50 border border-green-200 shadow-sm" data-id="element-563" data-mp="1406k6c2yhxv1">
                    <Check className="w-4 h-4 text-green-700 stroke-[3px]" data-id="element-564" data-mp="182tcsf7gbmta" />
                    <span className="text-green-700 text-xs md:text-sm font-black uppercase tracking-widest" data-id="element-565" data-mp="u1gzkbjodzb3">
                      INCLUIDO GRATIS
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-8 px-4 bg-white" style={{
      paddingTop: "0px"
    }} data-id="element-566" data-mp="ahickzllhkji">
        <div className="container mx-auto max-w-6xl" data-id="element-567" data-mp="pdr9vvlxdnta">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-568" data-mp="pb4s735blrxc">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-569" data-mp="yc2yjs38ebj3">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto uppercase font-bold" data-id="element-570" data-mp="184ig4oam8y3g">
              CONSIGUE MÁS DE{' '}
              <span className="text-blue-600" data-id="element-571" data-mp="135xfwovd6ef0">300 PÁGINAS A TODO COLOR</span>{' '}
              CON ILUSTRACIONES REALISTAS
            </p>
          </motion.div>

          <div className="space-y-16" data-id="element-572" data-mp="1d321amhdy5ur">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="flex justify-center" data-id="element-573" data-mp="1d4d7esyazpp1">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl" data-id="element-574" data-mp="1p5mmdzfq393w">
                <LazyVideo src="https://cdn.magicpatterns.com/uploads/o7CL5oytsWzC7MznGGznLq/0427 (1).mp4" className="w-full" data-id="element-575" data-mp="1u34xnwh7q0f2" />
              </div>
            </motion.div>
            <ImageCarousel images={contentImages} data-id="element-576" data-mp="22vq3kbzqwcmh" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - WhatsApp style, right before FAQ */}
      <section className="py-8 px-4 bg-gradient-to-b from-gray-100 to-gray-50" style={{
      paddingBottom: "0px"
    }} data-id="element-577" data-mp="2cuxqnqwa7lms">
        <div className="container mx-auto max-w-2xl" data-id="element-578" data-mp="2htqtmpdxzu8p">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8" data-id="element-579" data-mp="2ht90w2qhny5j">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-580" data-mp="ngj67isaqkx3">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]" data-id="element-581" data-mp="30rtzstcv1meb" />)}
              <span className="text-sm font-bold ml-2 tracking-wide text-[#0A0090]" data-id="element-582" data-mp="2vxpu0dfbzket">
                4.9/5 (3,251+ Alumnos)
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-800 uppercase" data-id="element-583" data-mp="2n05ogsafr1ye">
              MÁS DE 6.250 ELECTRICISTAS SATISFECHOS
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-584" data-mp="3jmsk89o16lkx">
              4,9/5 — 487 reseñas verificadas
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-4 mb-8" data-id="element-585" data-mp="3annjqxswsi64">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <motion.div key={i} variants={itemVariants} className="flex items-start gap-3" data-id="element-586" data-mp="1za2zil1tic40" data-mpi={i}>
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-587" data-mp="1u9fc09cssdej">
                  {t.name.charAt(0)}
                </div>
                <div className="flex-grow" data-id="element-588" data-mp="36wvdatp6w00">
                  <div className="flex items-center gap-2 mb-1" data-id="element-589" data-mp="3kea6bzlmvv68">
                    <span className="font-bold text-sm text-gray-800" data-id="element-590" data-mp="3kftojvquogan">
                      {t.name}
                    </span>
                    {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-591" data-mp="gxtod2rjdf7l">
                        ✓ Verificado
                      </span>}
                  </div>
                  <div className="chat-bubble" data-id="element-592" data-mp="78a3xso6ddam">
                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-593" data-mp="77gtagm3i3n7">
                      {t.msg}
                    </p>
                    <p className="text-[10px] text-gray-400 text-right mt-1" data-id="element-594" data-mp="c4h392m2fqjl">
                      {t.time}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-595" data-mp="2pbp1yqke0zac">
              <button onClick={showMoreTestimonials} className="btn-shine bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 font-medium py-2.5 px-6 rounded-lg transition-colors text-sm" data-id="element-596" data-mp="2keqaygw0t8gd">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-597" data-mp="26env1079wp3r" />
        </div>
      </section>

      {/* COMBO CLOSING CTA */}
      <section className="px-4 py-10 bg-white" style={{
      paddingTop: "12px",
      paddingBottom: "11px"
    }} data-id="element-598" data-mp="21g43mk9wul40">
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 8
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="max-w-md mx-auto rounded-3xl border border-[#C59A3D]/70 bg-[#071A35] px-5 py-6 text-center shadow-[0_18px_40px_rgba(7,26,53,0.2)]" data-id="element-599" data-mp="2fflztkpjd0ne">
          <p className="inline-flex items-center rounded-full border border-[#C59A3D]/60 px-3 py-1 text-[10px] font-black tracking-[0.14em] text-[#E7C56A]" data-id="element-600" data-mp="5avtj4bgfwmq">
            ♛ TU OFERTA FINAL
          </p>

          <h2 className="mt-4 text-[25px] font-black leading-[1.05] text-white uppercase" data-id="element-601" data-mp="3ri3uzvj97jdh">
            NO TE LLEVES SOLO UN MANUAL.
            <span className="block mt-1 text-[#E7C56A]" data-id="element-602" data-mp="3mkbzkrjehf4j">LLEVATE EL SISTEMA COMPLETO.</span>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-300" data-id="element-603" data-mp="o2g7b06xi0g3">
            Electricidad industrial, programación PLC, variadores y circuitos de mando. Todo reunido en un mismo combo para seguir creciendo como técnico.
          </p>

          <div className="mt-5 flex justify-center border-y border-[#C59A3D]/25 py-3" data-id="element-604" data-mp="9chcn7el175d">
            <img src={offerDetails.combo.preview} alt={offerDetails.combo.previewAlt} className="w-[90%] max-h-[235px] object-contain drop-shadow-[0_14px_18px_rgba(0,0,0,0.35)]" loading="lazy" decoding="async" data-id="element-605" data-mp="16rmw94ybfoac" />
          </div>

          <p className="mt-3 text-[10px] font-black tracking-[0.12em] text-[#E7C56A]" data-id="element-606" data-mp="1aac6xq6lobb5">
            4 MANUALES PROFESIONALES <span className="mx-1.5 text-[#C59A3D]" data-id="element-607" data-mp="1eb4feh7nec6d">·</span> 15 BONOS <span className="mx-1.5 text-[#C59A3D]" data-id="element-608" data-mp="1j96016kq2jjy">·</span> ACCESO DIGITAL
          </p>

          <div className="mt-4 rounded-xl border border-[#C59A3D]/35 bg-white/5 px-3 py-2.5" data-id="element-609" data-mp="1o8iotwv5m0p6">
            <p className="text-xs font-black tracking-wide text-[#E7C56A]" data-id="element-610" data-mp="1t84yxxfor0tr">⚡ ELEGÍ HOY EL PACK MÁS COMPLETO</p>
            <p className="mt-1 text-[11px] leading-snug text-slate-300" data-id="element-611" data-mp="1x8ym2olb0bll">Accedé a todo el material incluido en una sola compra.</p>
          </div>

          <div className="mt-4" data-id="element-612" data-mp="220ytt37m88sq">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400" data-id="element-613" data-mp="26za1p8eco98b">Combo Industrial Completo</p>
            <div className="mt-1 flex items-baseline justify-center gap-2" data-id="element-614" data-mp="2bywejgq9nngl">
              <span className="text-5xl font-black tracking-tighter text-[#E7C56A]" data-id="element-615" data-mp="2gxh84xb0j8w2">{offerDetails.combo.price}</span>
              <span className="text-sm font-black text-slate-400" data-id="element-616" data-mp="1r8sl7d0uomii">ARS</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-white" data-id="element-617" data-mp="1mah6oozm1nty">
              <CreditCard className="w-3.5 h-3.5 text-[#E7C56A]" data-id="element-618" data-mp="20yvdfb05dwfs" />
              Hasta 3 cuotas sin interés
            </div>
          </div>

          <motion.button whileHover={shouldReduceMotion ? undefined : {
          y: -2
        }} whileTap={{
          y: 2,
          scale: 0.99
        }} onClick={handleComboPurchaseClick} className={`mt-5 w-full whitespace-nowrap rounded-[18px] border border-[#D6B15D] bg-[linear-gradient(105deg,#19C76B,#079A4D,#19C76B)] py-4 px-3 text-sm font-black tracking-tight text-white shadow-[0_6px_0_#056B35,0_12px_18px_rgba(5,107,53,0.25)] active:shadow-[0_2px_0_#056B35,0_4px_8px_rgba(5,107,53,0.2)] ${shouldReduceMotion ? '' : 'animate-premium-cta-shine'}`} data-id="element-619" data-mp="1w6u2bsepubrc">
            QUIERO EL COMBO COMPLETO →
          </motion.button>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[10px] font-black tracking-wide text-[#BBF7D0]" data-id="element-620" data-mp="kmgbjzmts7v5">
            <span className="flex items-center gap-1" data-id="element-621" data-mp="317216w9qv7sb"><Zap className="w-3.5 h-3.5" data-id="element-622" data-mp="3160gs16u98xx" /> ACCESO INMEDIATO</span>
            <span className="flex items-center gap-1" data-id="element-623" data-mp="2mixj9uiin0al"><Lock className="w-3.5 h-3.5" data-id="element-624" data-mp="2mhw3jcqk1pmj" /> PAGO SEGURO</span>
            <span className="flex items-center gap-1" data-id="element-625" data-mp="2rgqmceoive6n"><Shield className="w-3.5 h-3.5" data-id="element-626" data-mp="2rfnxecarfltd" /> GARANTÍA DE 30 DÍAS</span>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 bg-white" style={{
      paddingBottom: "5px"
    }} data-id="element-627" data-mp="2hj2srs2nkcq2">
        <div className="container mx-auto max-w-3xl" data-id="element-628" data-mp="2hgztehfm178o">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-629" data-mp="23kuwznhcrhgh">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-630" data-mp="1t857jpmziiy">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-631" data-mp="6qhzpdrvukdn">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-3" data-id="element-632" data-mp="1xutfl6j9njq5">
            <motion.div variants={itemVariants} data-id="element-633" data-mp="1trwrx1evcxfc">
              <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-634" data-mp="1ov6deiipq9w1" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-635" data-mp="1a5h605k119nb">
              <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-636" data-mp="1a6a30et6oks9" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-637" data-mp="w6hg6uks2opk">
              <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-638" data-mp="37tkyg59bvhgu" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-639" data-mp="3hstowo4zidyg">
              <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-640" data-mp="3lt3tbwd4owb7" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-641" data-mp="2u1ekmptndmkk">
              <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-642" data-mp="2u267nc96ibz8" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-643" data-mp="32vsx6kmsmizz">
              <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-644" data-mp="26clk0g22yqjm" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-645" data-mp="2acma4xyjkq6i">
              <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-646" data-mp="3doupky1pq1tw" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-647" data-mp="38nnhsyzkl2n8">
              <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-648" data-mp="3nexgmsp9x5pl" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-649" data-mp="2uqic503rq5si">
              <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-650" data-mp="2prf91l7cm9n6" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-651" data-mp="2zoa8q7fcyg31">
              <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-652" data-mp="j0frantgsniu" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-653" data-mp="exsyajl24wk2">
              <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-654" data-mp="f16wrgqlolm8" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 px-4 bg-white border-t border-gray-100" style={{
      paddingTop: "11px",
      paddingBottom: "5px"
    }} data-id="element-655" data-mp="ckz5j6ypowm6">
        <div className="container mx-auto max-w-2xl text-center" data-id="element-656" data-mp="hjsjt1ltorne">
          <p className="text-[#FF0000] font-black text-sm md:text-base mb-6 uppercase tracking-wider" data-id="element-657" data-mp="158bjisqt3w3v">
            APROVECHA AHORA: NO ENCONTRARÁS ESTE PRECIO MÁS ADELANTE.
          </p>
          <div className="bg-[#E8F5E9] p-1.5 rounded-[22px] shadow-sm" data-id="element-658" data-mp="vjbcqn62bwf4">
            <motion.button whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            document.getElementById('compra')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="w-full bg-gradient-to-b from-[#22C55E] to-[#16A34A] hover:from-[#16A34A] hover:to-[#15803d] text-white rounded-[18px] py-6 px-4 shadow-[0_6px_0_#14532d,0_15px_20px_rgba(0,0,0,0.3)] active:shadow-[0_0px_0_#14532d,0_0px_0_rgba(0,0,0,0.2)] active:translate-y-[6px] transition-all flex flex-col items-center justify-center gap-1 relative overflow-hidden animate-cta-pulse btn-shine border border-[#4ade80]" data-id="element-659" data-mp="vh7diij3zll6">
              <span className="text-2xl md:text-3xl font-black uppercase tracking-tight relative z-10" data-id="element-660" data-mp="3bb8f1vs6xb3q">
                ¡SÍ! QUIERO MEJORAR MI CARRERA
              </span>
            </motion.button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 mb-2" data-id="element-661" data-mp="7ui6b9584jyc">
              <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-662" data-mp="2t2hr73u7vb7">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-663" data-mp="3u0eyxm4q08r4">
                  <Zap className="w-3.5 h-3.5 text-green-600" data-id="element-664" data-mp="2nlmfv8btivvq" />
                </div>
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-665" data-mp="2il8jwonhrce2">Acceso Inmediato</span>
              </div>
              <div className="hidden sm:block w-1 h-1 rounded-full bg-green-300" data-id="element-666" data-mp="28j4me9jom7nu"></div>
              <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-667" data-mp="2dhftwdmi862w">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-668" data-mp="2ieg2owaqnmxp">
                  <Shield className="w-3.5 h-3.5 text-green-600" data-id="element-669" data-mp="2n7ixf7r952fh" />
                </div>
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-670" data-mp="2r8kq15qnyg8h">Garantía de 30 Días</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4" data-id="element-671" data-mp="317a7ixendyo7">
        <div className="container mx-auto max-w-6xl flex flex-col items-center" data-id="element-672" data-mp="3651x3krtl8p8">
          <img src="https://cdn.magicpatterns.com/uploads/3vpvfNg4VHgy4RAHHhGhrN/Diseno_sin_titulo.webp" alt="Maestro Eléctrico Logo" className="w-24 h-24 md:w-32 md:h-32 object-contain mb-6 opacity-90" loading="lazy" data-id="element-673" data-mp="362zalapvlxp9" />
          <p className="text-center text-sm" data-id="element-674" data-mp="bmuh2pz85sb5">
            © 2025 Maestro Eléctrico. Todos los derechos reservados.
          </p>
          <p className="text-center text-sm mt-2" data-id="element-675" data-mp="gkmaxpjfjohz">
            Diagramas y circuitos de mando para profesionales.
          </p>
          <p className="text-center text-sm mt-4 text-gray-400" data-id="element-676" data-mp="ko22w8kk10oc">
            Soporte: soportecodexlabs@gmail.com
          </p>
        </div>
      </footer>
    </div>;
}